import React, { useState, useEffect, useCallback } from 'react';
import { api } from './lib/api';

// ─── Portal (clinic subdomain view) ───────────────────────────────────────────
// The practitioner portal is served per-clinic on a subdomain of the platform
// domain, e.g. nsc.newpatientengine.com. `app` / `www` / root render the admin app.
// The subdomain (first label) is the clinic slug — sent to /api/portal, which
// validates it against the clinic's portal_slug. So a new clinic works with zero
// code changes: point their slug at the wildcard domain and set portal_slug in the DB.
const RESERVED_SUBS = new Set(['app', 'www', 'admin', 'portal']);
const hostLabels = window.location.hostname.split('.');
const hasSubdomain = hostLabels.length >= 3 || (hostLabels.length === 2 && hostLabels[1] === 'localhost');
const portalSlug = (hasSubdomain && !RESERVED_SUBS.has(hostLabels[0])) ? hostLabels[0] : null;

const NPE = {
  ink:'#0A1628', card:'#122A4E', panel:'#0F2140', line:'rgba(146,178,224,.14)', lineStrong:'rgba(146,178,224,.22)',
  blue:'#2E8BE6', blue2:'#5FB4F7', blueTint:'rgba(46,139,230,.16)',
  text:'#EAF1FB', slate:'#9fb2d0', slate2:'#7C8AA6',
  good:'#41d98b', goodTint:'rgba(65,217,139,.13)', red:'#f0846b', redTint:'rgba(240,132,107,.14)',
  amber:'#FBBF24', amberTint:'rgba(251,191,36,.13)',
  font:'-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,Roboto,"Helvetica Neue",Arial,sans-serif',
};
// First name + last initial (privacy on the practitioner dashboard)
const shortName = full => {
  if (!full) return 'Unknown patient';
  const p = full.trim().split(/\s+/);
  return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : p[0];
};

function PortalApp() {
  const [pin, setPin]     = useState('');
  const [error, setError] = useState('');
  const [data, setData]   = useState(null);
  const [reviewed, setReviewed] = useState(new Set()); // "New" cleared
  const [noted, setNoted]       = useState(new Set()); // "Needs attention" cleared
  const [sent, setSent]         = useState(new Set()); // report texted

  const submit = async e => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: portalSlug, pin }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'Incorrect PIN'); return; }
      setData(json);
    } catch { setError('Connection error — try again'); }
  };

  if (!data) return (
    <div style={{ minHeight: '100vh', background: NPE.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: NPE.font, padding: 20 }}>
      <div style={{ background: NPE.card, border: `1px solid ${NPE.line}`, borderRadius: 16, padding: '44px 36px', width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <img src="/npe-logo-dark.svg" alt="New Patient Engine" style={{ height: 22, marginBottom: 22 }} />
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 6 }}>Practice Dashboard</h1>
        <p style={{ fontSize: 14, color: NPE.slate2, marginBottom: 26 }}>Enter your PIN to view your clinic dashboard</p>
        <form onSubmit={submit}>
          <input
            type="password" inputMode="numeric" placeholder="PIN"
            value={pin} onChange={e => setPin(e.target.value)}
            style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${error ? NPE.red : NPE.lineStrong}`, borderRadius: 10, fontSize: 18, textAlign: 'center', letterSpacing: 8, outline: 'none', marginBottom: 12, boxSizing: 'border-box', fontFamily: NPE.font, background: NPE.panel, color: '#fff' }}
          />
          {error && <p style={{ color: NPE.red, fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <button type="submit" style={{ width: '100%', padding: '12px', background: NPE.blue, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: NPE.font }}>
            Enter →
          </button>
        </form>
      </div>
    </div>
  );

  const { client, stats = {}, surveyReports = [] } = data;
  const syncOk = stats.lastSyncStatus === 'success';

  // Group survey rows by patient → first (baseline) + latest score
  const groups = new Map();
  for (const r of surveyReports) {
    const arr = groups.get(r.contact_id) || [];
    arr.push(r); groups.set(r.contact_id, arr);
  }
  const patients = [...groups.values()].map(list => {
    list.sort((a, b) => (a.survey_number || 0) - (b.survey_number || 0));
    const first = list[0], latest = list[list.length - 1];
    const fs = Math.round(parseFloat(first.score_composite));
    const ls = Math.round(parseFloat(latest.score_composite));
    const days = (Date.now() - new Date(latest.completed_at)) / 86400000;
    return {
      id: latest.contact_id, name: shortName(latest.contact_name),
      first: fs, latest: ls, intakeOnly: list.length === 1,
      stageNum: latest.survey_number, date: latest.completed_at,
      delta: ls - fs, fresh: days <= 3,
    };
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const isNew  = p => p.fresh && !reviewed.has(p.id);
  const isAttn = p => !p.intakeOnly && p.delta < 0 && !noted.has(p.id);
  const add = (setter, id) => setter(prev => new Set(prev).add(id));

  const repeats = patients.filter(p => !p.intakeOnly);
  const avgImp = repeats.length
    ? Math.round(repeats.reduce((s, p) => s + ((p.latest - p.first) / Math.max(p.first, 1)) * 100, 0) / repeats.length)
    : null;

  const kpis = [
    { lbl: 'Patients Tracked', big: patients.length, sub: 'with a survey on file', color: '#fff' },
    { lbl: 'Avg Improvement',  big: avgImp == null ? '—' : `${avgImp > 0 ? '+' : ''}${avgImp}%`, sub: 'baseline → latest', color: '#fff' },
    { lbl: 'New Reports',      big: patients.filter(isNew).length,  sub: 'ready to review', color: NPE.blue2 },
    { lbl: 'Needs Attention',  big: patients.filter(isAttn).length, sub: 'declining · mark once noted', color: NPE.amber },
  ];

  const th = { textAlign: 'left', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: NPE.slate2, padding: '12px 20px', borderBottom: `1px solid ${NPE.line}` };
  const td = { padding: '14px 20px', borderBottom: `1px solid ${NPE.line}`, fontSize: 14, color: NPE.text };
  const fmt = d => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—';

  return (
    <div style={{ minHeight: '100vh', background: NPE.ink, fontFamily: NPE.font, padding: '26px 20px', color: NPE.text }}>
      <style>{`@media(max-width:640px){.npe-kpis{grid-template-columns:repeat(2,1fr)!important}.npe-hide{display:none}}`}</style>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 20, borderBottom: `1px solid ${NPE.line}`, marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <img src="/npe-logo-dark.svg" alt="New Patient Engine" style={{ height: 20 }} />
            <div style={{ width: 1, height: 32, background: NPE.lineStrong }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-.3px' }}>{client.name}</div>
              <div style={{ fontSize: 12, color: NPE.slate2 }}>Powered by New Patient Engine</div>
            </div>
          </div>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: syncOk ? NPE.good : NPE.amber }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: syncOk ? NPE.good : NPE.amber, boxShadow: `0 0 0 3px ${syncOk ? NPE.goodTint : NPE.amberTint}` }} />
            {syncOk ? 'Live' : (stats.lastSyncStatus || 'Pending')}
          </span>
        </div>

        {/* KPIs */}
        <div className="npe-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 26 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ background: NPE.card, border: `1px solid ${NPE.line}`, borderRadius: 16, padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: NPE.slate2, marginBottom: 10 }}>{k.lbl}</div>
              <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1, color: k.color }}>{k.big}</div>
              <div style={{ fontSize: 12, color: NPE.slate, marginTop: 5 }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Progress Reports */}
        <div style={{ background: NPE.panel, border: `1px solid ${NPE.line}`, borderRadius: 18, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${NPE.line}` }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Progress Reports</span>
            <span style={{ fontSize: 13, color: NPE.slate2 }}>{patients.length} patient{patients.length !== 1 ? 's' : ''} · first → latest</span>
          </div>
          {patients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: NPE.slate2 }}>
              <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>📋</div>
              <div style={{ fontWeight: 700, color: '#fff', marginBottom: 4 }}>No surveys yet</div>
              <div style={{ fontSize: 13 }}>Patient reports will appear here once surveys are completed.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead><tr>
                  <th style={th}>Patient</th>
                  <th style={th} className="npe-hide">Stage</th>
                  <th style={th}>First → Latest</th>
                  <th style={th} className="npe-hide">Change</th>
                  <th style={th} className="npe-hide">Updated</th>
                  <th style={th}></th>
                </tr></thead>
                <tbody>
                  {patients.map(p => {
                    const attn = isAttn(p), fresh = isNew(p);
                    const stage = p.intakeOnly
                      ? <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(146,178,224,.12)', color: NPE.slate }}>Intake</span>
                      : <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: NPE.blueTint, color: NPE.blue2 }}>Progress #{p.stageNum - 1}</span>;
                    let flow, delta;
                    if (p.intakeOnly) {
                      flow = <><span style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{p.first}</span> <span style={{ color: NPE.slate2, fontSize: 12 }}>baseline</span></>;
                      delta = <span style={{ fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: 'rgba(146,178,224,.12)', color: NPE.slate2 }}>—</span>;
                    } else {
                      const c = p.delta > 0 ? [NPE.goodTint, NPE.good] : p.delta < 0 ? [NPE.redTint, NPE.red] : ['rgba(146,178,224,.12)', NPE.slate2];
                      flow = <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}><span style={{ color: NPE.slate2 }}>{p.first}</span><span style={{ color: NPE.slate2 }}>→</span><span style={{ color: '#fff', fontSize: 15 }}>{p.latest}</span></span>;
                      delta = <span style={{ fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: c[0], color: c[1] }}>{p.delta > 0 ? '+' : ''}{p.delta}</span>;
                    }
                    return (
                      <tr key={p.id} style={attn ? { boxShadow: `inset 3px 0 0 ${NPE.amber}` } : undefined}>
                        <td style={td}><span style={{ fontWeight: 700, color: '#fff' }}>{p.name}</span>{fresh && <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20, background: NPE.blueTint, color: NPE.blue2, marginLeft: 9 }}>New</span>}</td>
                        <td style={td} className="npe-hide">{stage}</td>
                        <td style={td}>{flow}</td>
                        <td style={td} className="npe-hide">{delta}</td>
                        <td style={td} className="npe-hide"><span style={{ color: NPE.slate2, fontSize: 13 }}>{fmt(p.date)}</span></td>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                            {fresh && <button onClick={() => add(setReviewed, p.id)} style={{ fontSize: 12, fontWeight: 700, color: NPE.blue2, background: NPE.blueTint, border: 'none', borderRadius: 7, padding: '6px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Review completed</button>}
                            {attn && <button onClick={() => add(setNoted, p.id)} style={{ fontSize: 12, fontWeight: 700, color: NPE.amber, background: NPE.amberTint, border: 'none', borderRadius: 7, padding: '6px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Mark noted</button>}
                            {sent.has(p.id)
                              ? <span style={{ fontSize: 12, fontWeight: 700, color: NPE.good, whiteSpace: 'nowrap' }}>✓ Sent</span>
                              : <button onClick={() => add(setSent, p.id)} style={{ fontSize: 12, fontWeight: 700, color: NPE.slate, background: 'transparent', border: `1px solid ${NPE.lineStrong}`, borderRadius: 7, padding: '6px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Send report</button>}
                            <a href={`/api/progress-print?client_id=${client.id}&contact_id=${p.id}`} target="_blank" rel="noopener noreferrer" style={{ color: NPE.blue2, fontWeight: 700, fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap' }}>View ↗</a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', fontSize: 11, color: NPE.slate2, marginTop: 24, opacity: 0.6 }}>Powered by New Patient Engine</div>
      </div>
    </div>
  );
}

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const B = {
  teal:      '#00D4A0',
  tealDark:  '#00a87e',
  tealLight: '#e0faf4',
  tealBorder:'rgba(0,212,160,0.25)',
  navy:      '#080F1C',
  red:       '#ef4444',
  redLight:  'rgba(239,68,68,0.08)',
  redBorder: 'rgba(239,68,68,0.25)',
  amber:     '#f59e0b',
  serif:     "'Plus Jakarta Sans', system-ui, sans-serif",
  sans:      "'Inter', system-ui, sans-serif",
};

// ─── Theme tokens (light / dark) ──────────────────────────────────────────────
const THEMES = {
  light: {
    bg:          '#f3f7fb',
    card:        '#ffffff',
    border:      'rgba(0,0,0,0.08)',
    text:        '#374151',
    textStrong:  '#080F1C',
    muted:       '#6b7a8d',
    inputBg:     '#f3f7fb',
    shadow:      '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05)',
    shadowMd:    '0 2px 8px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.10)',
    btnGhost:    { bg: 'transparent', color: '#6b7a8d', border: 'rgba(0,0,0,0.12)' },
  },
  dark: {
    bg:          '#080F1C',
    card:        '#0e1929',
    border:      'rgba(255,255,255,0.08)',
    text:        '#94a3b8',
    textStrong:  '#f1f5f9',
    muted:       '#4b5f75',
    inputBg:     '#131f30',
    shadow:      '0 1px 4px rgba(0,0,0,0.3), 0 4px 16px rgba(0,0,0,0.2)',
    shadowMd:    '0 2px 8px rgba(0,0,0,0.4), 0 12px 40px rgba(0,0,0,0.4)',
    btnGhost:    { bg: 'transparent', color: '#4b5f75', border: 'rgba(255,255,255,0.1)' },
  },
};

// ─── Logo ─────────────────────────────────────────────────────────────────────
function Logo() {
  return (
    <svg width="134" height="32" viewBox="0 0 134 32" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bolt-g" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#00D4A0" /><stop offset="100%" stopColor="#00a87e" />
        </linearGradient>
      </defs>
      <polygon points="18,1 6,16 12,16 7,31 24,14 18,14 23,1" fill="url(#bolt-g)" />
      <polygon points="18,1 13,8 16,8 11,16 17,10 20,2" fill="rgba(255,255,255,0.28)" />
      <text x="30" y="23" fontSize="19" fontWeight="900" letterSpacing="-0.6" fill="#ffffff"
        fontFamily="'Plus Jakarta Sans', system-ui, sans-serif">
        Bolt<tspan fill="#00D4A0">Sync</tspan>
      </text>
    </svg>
  );
}

// ─── Theme toggle button ───────────────────────────────────────────────────────
function ThemeToggle({ dark, onToggle }) {
  return (
    <button onClick={onToggle} title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        width: 40, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: dark ? B.teal : 'rgba(255,255,255,0.2)',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        display: 'flex', alignItems: 'center', padding: '2px 3px',
      }}>
      <span style={{
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, transition: 'transform 0.2s',
        transform: dark ? 'translateX(16px)' : 'translateX(0)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      }}>
        {dark ? '🌙' : '☀️'}
      </span>
    </button>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function Badge({ status }) {
  const ok = status === 'success' || status === 'Synced' || status === 'Live';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
      borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: ok ? B.tealLight : B.redLight,
      color: ok ? B.tealDark : B.red,
      border: `1px solid ${ok ? B.tealBorder : B.redBorder}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: ok ? B.teal : B.red }} />
      {status || 'Pending'}
    </span>
  );
}

// ─── PMS badge ────────────────────────────────────────────────────────────────
const CLINIKO_BADGE = { bg: 'rgba(0,212,160,0.1)', color: '#00a87e', border: 'rgba(0,212,160,0.25)' };
function PmsBadge() {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.03em',
      background: CLINIKO_BADGE.bg, color: CLINIKO_BADGE.color, border: `1px solid ${CLINIKO_BADGE.border}`,
    }}>
      Cliniko
    </span>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function Empty({ icon, title, sub, t }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: t.muted }}>
      <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.35 }}>{icon}</div>
      <div style={{ fontWeight: 700, color: t.textStrong, fontFamily: B.serif, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13 }}>{sub}</div>
    </div>
  );
}

// ─── Call Logs section ────────────────────────────────────────────────────────
const ACTION_COLOURS = { booked: '#00D4A0', rescheduled: '#3b82f6', cancelled: '#f87171', 'no-booking': '#f59e0b' };

function CallLogsSection({ callLogs, t }) {
  const [expanded, setExpanded] = React.useState(null);
  const cardHead = { padding: '16px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const tdS = { padding: '10px 16px', borderBottom: `1px solid ${t.border}`, verticalAlign: 'top' };
  return (
    <div className="bs-card" style={{ background: t.card, border: `1px solid ${t.border}`, marginBottom: 20, boxShadow: t.shadow }}>
      <div style={cardHead}>
        <span style={{ fontSize: 15, fontWeight: 700, color: t.textStrong, fontFamily: B.serif }}>Kayli Call Logs</span>
        <span style={{ fontSize: 12, color: t.muted }}>{callLogs.length} recent calls</span>
      </div>
      {callLogs.length === 0 ? (
        <Empty icon="📞" title="No calls yet" sub="Call logs will appear after Kayli handles her first call." t={t} />
      ) : (
        <div className="bs-tbl-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 560 }}>
            <thead><tr>
              {['Time','Clinic','Patient','Caller','Outcome','Appointment','Practitioner','Recording','Transcript'].map(h => (
                <th key={h} style={{ ...tdS, fontWeight: 600, color: t.muted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', background: t.bg }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{callLogs.map((log, i) => (
              <React.Fragment key={i}>
                <tr style={{ cursor: log.transcript ? 'pointer' : 'default' }} onClick={() => log.transcript && setExpanded(expanded === i ? null : i)}>
                  <td style={{ ...tdS, color: t.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{log.created_at ? new Date(log.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}</td>
                  <td style={{ ...tdS, fontWeight: 600, color: t.textStrong }}>{log.client_name || '—'}</td>
                  <td style={{ ...tdS, color: t.textStrong }}>{log.patient_name || <span style={{ color: t.muted }}>Unknown</span>}</td>
                  <td style={{ ...tdS, color: t.muted }}>{log.caller_phone || '—'}</td>
                  <td style={tdS}>
                    {log.action ? (
                      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600, background: ACTION_COLOURS[log.action] + '22', color: ACTION_COLOURS[log.action] }}>
                        {log.action}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ ...tdS, fontSize: 13 }}>{log.appointment_time ? <>{log.appointment_type && <span style={{ display: 'block', fontWeight: 600, color: t.textStrong }}>{log.appointment_type}</span>}{log.appointment_time}</> : '—'}</td>
                  <td style={{ ...tdS, fontSize: 13, color: t.muted }}>{log.practitioner || '—'}</td>
                  <td style={{ ...tdS, fontSize: 12 }} onClick={e => e.stopPropagation()}>
                    {log.recording_url
                      ? <a href={log.recording_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: B.teal, fontWeight: 600, textDecoration: 'none' }}>▶ Play</a>
                      : <span style={{ color: t.muted }}>—</span>}
                  </td>
                  <td style={{ ...tdS, fontSize: 12, color: B.teal }}>{log.transcript ? (expanded === i ? 'Hide ▲' : 'View ▼') : '—'}</td>
                </tr>
                {expanded === i && log.transcript && (
                  <tr>
                    <td colSpan={9} style={{ padding: '0 16px 16px', background: t.bg }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13, color: t.muted, lineHeight: 1.6, maxHeight: 300, overflowY: 'auto', padding: '12px 16px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 8 }}>{log.transcript}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Timezone options ─────────────────────────────────────────────────────────
const TIMEZONES = [
  { label: 'Queensland (Brisbane)',      value: 'Australia/Brisbane' },
  { label: 'NSW / VIC / ACT (Sydney)',   value: 'Australia/Sydney' },
  { label: 'South Australia (Adelaide)', value: 'Australia/Adelaide' },
  { label: 'Western Australia (Perth)',  value: 'Australia/Perth' },
  { label: 'Northern Territory (Darwin)',value: 'Australia/Darwin' },
  { label: 'Tasmania (Hobart)',          value: 'Australia/Hobart' },
  { label: 'New Zealand (Auckland)',     value: 'Pacific/Auckland' },
  { label: 'United Kingdom (London)',    value: 'Europe/London' },
  { label: 'United States — Eastern',   value: 'America/New_York' },
  { label: 'United States — Pacific',   value: 'America/Los_Angeles' },
];

// ─── Add Client modal ─────────────────────────────────────────────────────────
function AddClientModal({ onClose, onAdd, t }) {
  const [form, setForm] = useState({
    name: '', location: '',
    clinikoApiKey: '', clinikoShard: 'au1',
    ghlApiKey: '', ghlLocationId: '',
    timezone: 'Australia/Brisbane',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try { await onAdd(form); onClose(); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const inputStyle = {
    padding: '9px 12px', borderRadius: 8, border: `1px solid ${t.border}`,
    background: t.inputBg, color: t.textStrong, fontSize: 14,
    width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: B.sans,
  };
  const labelStyle = { fontSize: 12, color: t.muted, display: 'block', marginBottom: 5, fontWeight: 600, letterSpacing: '0.04em' };
  const fg = { marginBottom: 14 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,15,28,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(6px)' }}
      onClick={onClose}>
      <div style={{ background: t.card, borderRadius: 16, padding: 32, maxWidth: 540, width: '92%', maxHeight: '88vh', overflow: 'auto', boxShadow: t.shadowMd, border: `1px solid ${t.border}` }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: t.textStrong, fontFamily: B.serif, margin: '0 0 4px' }}>Add New Client</h3>
        <p style={{ fontSize: 13, color: t.muted, margin: '0 0 24px' }}>Connect a clinic's PMS to GoHighLevel via BoltSync.</p>
        {error && <div style={{ color: B.red, background: B.redLight, border: `1px solid ${B.redBorder}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <form onSubmit={handleSubmit}>

          {/* Practice details */}
          <div className="bs-modal-g2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div style={fg}><label style={labelStyle}>Practice Name *</label><input style={inputStyle} value={form.name} onChange={set('name')} required /></div>
            <div style={fg}><label style={labelStyle}>Location</label><input style={inputStyle} value={form.location} onChange={set('location')} placeholder="e.g. Brisbane, QLD" /></div>
          </div>

          {/* Cliniko fields */}
          <div className="bs-modal-g2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div style={fg}><label style={labelStyle}>Cliniko API Key *</label><input style={inputStyle} type="password" value={form.clinikoApiKey} onChange={set('clinikoApiKey')} required /></div>
            <div style={fg}><label style={labelStyle}>Shard</label><input style={inputStyle} value={form.clinikoShard} onChange={set('clinikoShard')} placeholder="au1" /></div>
          </div>

          {/* GHL fields */}
          <div className="bs-modal-g2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div style={fg}><label style={labelStyle}>GHL API Key *</label><input style={inputStyle} type="password" value={form.ghlApiKey} onChange={set('ghlApiKey')} required /></div>
            <div style={fg}><label style={labelStyle}>GHL Location ID *</label><input style={inputStyle} value={form.ghlLocationId} onChange={set('ghlLocationId')} required /></div>
          </div>

          <div style={fg}>
            <label style={labelStyle}>Clinic Timezone</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.timezone} onChange={set('timezone')}>
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${t.border}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: 'transparent', color: t.muted, fontFamily: B.sans }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: B.teal, color: B.navy, fontFamily: B.sans, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Connecting…' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── BoltStats: CAC Manager (admin only) ─────────────────────────────────────
function CacManager({ clients, t, ghostBtn, primaryBtn }) {
  const [open, setOpen]               = useState(false);
  const [clientId, setClientId]       = useState('');
  const [entries, setEntries]         = useState([]);
  const [month, setMonth]             = useState(() => new Date().toISOString().slice(0, 7));
  const [spend, setSpend]             = useState('');
  const [notes, setNotes]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [backfillWeeks, setBackfillWeeks] = useState(24);
  const [backfillProgress, setBackfillProgress] = useState(null);
  const [statsFrom, setStatsFrom]     = useState('');

  const load = async (id) => {
    if (!id) return;
    try {
      const d = await api.getCac(id);
      setEntries(d.entries || []);
    } catch (err) {
      setMsg(err.message);
    }
  };

  const handleClientChange = e => {
    const id = e.target.value;
    setClientId(id);
    load(id);
    setBackfillProgress(null);
    // Pre-fill the stats start date from client record
    const client = clients.find(c => String(c.id) === String(id));
    setStatsFrom(client?.stats_start_date ? client.stats_start_date.slice(0, 10) : '');
  };

  const handleSaveStatsFrom = async () => {
    if (!clientId) return;
    setSaving(true); setMsg('');
    try {
      await api.updateClient(clientId, { stats_start_date: statsFrom || null });
      setMsg('Stats start date saved — re-run a backfill to recalculate history.');
    } catch (err) { setMsg(err.message); }
    finally { setSaving(false); }
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!clientId || !month || !spend) return;
    setSaving(true); setMsg('');
    try {
      await api.saveCac({ client_id: parseInt(clientId), month: `${month}-01`, marketing_spend: parseFloat(spend), notes });
      setSpend(''); setNotes(''); setMsg('Saved.');
      load(clientId);
    } catch (err) { setMsg(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (entryMonth) => {
    await api.deleteCac(clientId, entryMonth);
    load(clientId);
  };

  const handleSnapshot = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await api.triggerSnapshot();
      setMsg(`Snapshot done: ${r.results?.filter(x => x.status === 'ok').length} client(s) updated.`);
    } catch (err) { setMsg(err.message); }
    finally { setSaving(false); }
  };

  const handleBackfill = async () => {
    if (!clientId) { setMsg('Select a client first'); return; }
    setSaving(true); setMsg('');
    let total = 0;
    let offset = 0;
    let done = false;
    // Keep calling until all weeks are processed
    while (!done) {
      try {
        const r = await api.backfillStats(clientId, backfillWeeks, offset);
        total += r.processed;
        offset = r.offset;
        done = r.done;
        setBackfillProgress({ processed: total, remaining: r.remaining, done: r.done });
        if (r.processed === 0) break;
      } catch (err) {
        setMsg(err.message);
        break;
      }
    }
    setSaving(false);
    if (done) setMsg(`Backfill complete — ${total} months of history added.`);
  };

  const handleResync = async () => {
    if (!clientId) { setMsg('Select a client first'); return; }
    if (!window.confirm('This resets the appointments cursor so the next cron run re-syncs all historical appointments (to populate appointment types). Continue?')) return;
    setSaving(true); setMsg('');
    try {
      const r = await api.resyncAppts(clientId);
      setMsg(r.message);
    } catch (err) { setMsg(err.message); }
    finally { setSaving(false); }
  };

  const inp = {
    padding: '8px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
    background: t.inputBg, color: t.textStrong, fontSize: 13, fontFamily: B.sans,
  };

  return (
    <div className="bs-card" style={{ background: t.card, border: `1px solid ${t.border}`, marginBottom: 20, boxShadow: t.shadow }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: t.textStrong, fontFamily: B.serif }}>BoltStats — History & CAC</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={ghostBtn} onClick={handleSnapshot} disabled={saving}>
            {saving ? 'Running…' : 'Snapshot Now'}
          </button>
          <button style={ghostBtn} onClick={() => setOpen(o => !o)}>
            {open ? 'Hide ▲' : 'Marketing Spend ▼'}
          </button>
        </div>
      </div>

      {/* ── Backfill + Resync row ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: '12px 14px', background: t.bg, borderRadius: 10, border: `1px solid ${t.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: t.muted, flex: '0 0 auto' }}>Client:</div>
        <select style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.textStrong, fontSize: 13, fontFamily: B.sans }}
          value={clientId} onChange={handleClientChange}>
          <option value="">Select…</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <div style={{ fontSize: 12, fontWeight: 600, color: t.muted }}>Backfill:</div>
        <select style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.textStrong, fontSize: 13, fontFamily: B.sans }}
          value={backfillWeeks} onChange={e => setBackfillWeeks(parseInt(e.target.value))}>
          <option value={6}>6 months</option>
          <option value={12}>12 months</option>
          <option value={24}>24 months</option>
          <option value={36}>3 years</option>
        </select>
        <button style={primaryBtn} onClick={handleBackfill} disabled={saving || !clientId}>
          {saving && backfillProgress ? `Backfilling…` : 'Backfill History'}
        </button>

        <button style={{ ...ghostBtn, borderColor: 'rgba(245,158,11,0.4)', color: B.amber }}
          onClick={handleResync} disabled={saving || !clientId}
          title="Resets appointment cursor so next sync repopulates appointment types for historical records">
          Re-sync Appt Types
        </button>

        {backfillProgress && (
          <span style={{ fontSize: 12, color: backfillProgress.done ? B.teal : t.muted }}>
            {backfillProgress.done
              ? `Done — ${backfillProgress.processed} months backfilled`
              : `${backfillProgress.processed} months done, ${backfillProgress.remaining} remaining…`}
          </span>
        )}

        {/* Stats start date — filters out old patients from history-based metrics */}
        {clientId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.muted }}>Stats from:</div>
            <input type="date" style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.textStrong, fontSize: 13, fontFamily: B.sans }}
              value={statsFrom} onChange={e => setStatsFrom(e.target.value)} />
            <button style={ghostBtn} onClick={handleSaveStatsFrom} disabled={saving}>Save</button>
            {statsFrom && <button style={{ ...ghostBtn, fontSize: 11 }} onClick={() => setStatsFrom('')}>Clear</button>}
            <div style={{ fontSize: 11, color: t.muted }}>Only count appointments from this date — use for acquired practices with old patient data.</div>
          </div>
        )}
      </div>

      {msg && <div style={{ fontSize: 12, color: B.teal, marginBottom: 12 }}>{msg}</div>}
      {open && (
        <>
          <form onSubmit={handleSave} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 11, color: t.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Client</div>
              <select style={{ ...inp, minWidth: 180 }} value={clientId} onChange={handleClientChange} required>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, color: t.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Month</div>
              <input type="month" style={inp} value={month} onChange={e => setMonth(e.target.value)} required />
            </div>
            <div>
              <div style={{ fontSize: 11, color: t.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Marketing Spend ($)</div>
              <input type="number" min="0" step="0.01" style={{ ...inp, width: 130 }}
                value={spend} onChange={e => setSpend(e.target.value)} placeholder="e.g. 2000" required />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 11, color: t.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Notes (optional)</div>
              <input style={{ ...inp, width: '100%', boxSizing: 'border-box' }}
                value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Google + Meta" />
            </div>
            <button type="submit" style={primaryBtn} disabled={saving}>Save</button>
          </form>

          {entries.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                {['Month','Spend','New Patients','CAC','Notes',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: t.muted, fontWeight: 600, fontSize: 11, textAlign: 'left', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{entries.map((e, i) => (
                <tr key={i}>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: t.textStrong, fontWeight: 600 }}>
                    {new Date(e.month).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: t.text }}>${parseFloat(e.marketing_spend).toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: t.text }}>{e.new_patients_that_month ?? '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: B.teal, fontWeight: 700 }}>
                    {e.calculated_cac ? `$${parseFloat(e.calculated_cac).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: t.muted, fontSize: 12 }}>{e.notes || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}` }}>
                    <button onClick={() => handleDelete(e.month)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.red, fontSize: 12, fontFamily: B.sans }}>Remove</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ─── BoltStats ────────────────────────────────────────────────────────────────

// Flat definitions map — used for chart labels and formatting
const DEFS = {
  momentum_30d:        { label: 'Monthly Momentum',    color: '#10b981', prefix: '',  suffix: '' },
  momentum_7d:         { label: 'Weekly Momentum',     color: '#6366f1', prefix: '',  suffix: '' },
  tap_30d:             { label: 'Active Patients',     color: '#00D4A0', prefix: '',  suffix: '' },
  new_30d:             { label: 'New Patients/Mo',     color: '#6366f1', prefix: '',  suffix: '' },
  new_7d:              { label: 'New Patients/Wk',     color: '#818cf8', prefix: '',  suffix: '' },
  appts_30d:           { label: 'Total Appts/Mo',      color: '#10b981', prefix: '',  suffix: '' },
  appts_7d:            { label: 'Total Appts/Wk',      color: '#34d399', prefix: '',  suffix: '' },
  reactivations_30d:   { label: 'Reactivations/Mo',    color: '#f59e0b', prefix: '',  suffix: '' },
  reactivations_7d:    { label: 'Reactivations/Wk',    color: '#fbbf24', prefix: '',  suffix: '' },
  no_fwd_30d:          { label: 'No Fwd Appt (30d)',   color: '#ef4444', prefix: '',  suffix: '' },
  no_fwd_7d:           { label: 'No Fwd Appt (7d)',    color: '#f87171', prefix: '',  suffix: '' },
  avg_new_per_month:   { label: 'Avg New/Mo (12mo)',   color: '#a78bfa', prefix: '',  suffix: '' },
  avg_new_per_week:    { label: 'Avg New/Wk (12mo)',   color: '#c4b5fd', prefix: '',  suffix: '' },
  avg_react_per_month: { label: 'Avg Reactivations/Mo',color: '#fb923c', prefix: '',  suffix: '' },
  avg_react_per_week:  { label: 'Avg Reactivations/Wk',color: '#fdba74', prefix: '',  suffix: '' },
  vc1:                 { label: 'Patients (1+ visits)',  color: '#6366f1', prefix: '',  suffix: '' },
  vc2:                 { label: 'Patients (2+ visits)',  color: '#8b5cf6', prefix: '',  suffix: '' },
  vc3:                 { label: 'Patients (3+ visits)',  color: '#a78bfa', prefix: '',  suffix: '' },
  vc6:                 { label: 'Patients (6+ visits)',  color: '#10b981', prefix: '',  suffix: '' },
  vc12:                { label: 'Patients (12+ visits)', color: '#00D4A0', prefix: '',  suffix: '' },
  vc24:                { label: 'Patients (24+ visits)', color: '#06b6d4', prefix: '',  suffix: '' },
  pva:                 { label: 'PVA',                  color: '#a855f7', prefix: '',  suffix: '' },
  cva:                 { label: 'CVA',                  color: '#06b6d4', prefix: '$', suffix: '' },
  ltv:                 { label: 'LTV',                  color: '#84cc16', prefix: '$', suffix: '' },
};

// Keep for legacy table compat
const STAT_DEFS = [
  { key: 'net_momentum',         label: 'Net Momentum',          prefix: '',  suffix: '',  color: '#10b981', info: 'Net weekly patient change: new + reactivations − attrition', hidden: true },
  { key: 'tap_30d',              label: 'Active Patients',       prefix: '',  suffix: '',  color: '#00D4A0', info: 'Unique patients with a visit in the last 30 days' },
  { key: 'avg_weekly_new',       label: 'New Patients/Mo',       prefix: '',  suffix: '',  color: '#6366f1', info: 'New patients who started care this month (last 30 days)' },
  { key: 'avg_weekly_appts',     label: 'Total Appts/Mo',        prefix: '',  suffix: '',  color: '#10b981', info: 'Total non-cancelled appointments in the last 30 days' },
  { key: 'reactivations',        label: 'Reactivations',         prefix: '',  suffix: '',  color: '#f59e0b', info: 'Patients absent 90+ days who now have an upcoming appointment' },
  { key: 'attrition',            label: 'Attrition (Cancelled)', prefix: '',  suffix: '',  color: '#ef4444', info: 'Patients who cancelled or DNA\'d in the last 30 days with no upcoming appointments' },
  { key: 'attrition_no_rebooking', label: 'No Forward Booking', prefix: '',  suffix: '',  color: '#f97316', info: 'Patients who attended in the last 30 days but have NOT booked a future appointment — at risk' },
  { key: 'keep_visit_rate',      label: 'Keep Visit Rate',       prefix: '',  suffix: '%', color: '#3b82f6', info: 'Appointments kept vs did-not-arrive (where status is recorded)' },
  { key: 'stick_rate',           label: 'Stick Rate',            prefix: '',  suffix: '%', color: '#8b5cf6', info: 'Initial Consultation → ROF conversion rate' },
  { key: 'yes_to_care_rate',     label: 'Yes to Care',           prefix: '',  suffix: '%', color: '#ec4899', info: 'ROF patients who then booked a Standard Adjustment' },
  { key: 'churn_pre_12',         label: 'Churn (Pre-12)',        prefix: '',  suffix: '%', color: '#ef4444', info: 'Patients who stopped coming before visit 12 with no upcoming bookings' },
  { key: 'churn_pre_24',         label: 'Churn (12–24)',         prefix: '',  suffix: '%', color: '#f97316', info: 'Patients who stopped between visits 12 and 24 with no upcoming bookings' },
  { key: 'r4_stick_rate',        label: 'R4 Stick Rate',         prefix: '',  suffix: '%', color: '#14b8a6', info: 'Percentage of patients who reached visit 24' },
  { key: 'r4_reconversion_rate', label: 'R4 Re-conversion',      prefix: '',  suffix: '%', color: '#0ea5e9', info: 'Post-visit-24 patients who recommitted to another care phase' },
  { key: 'pva',                  label: 'PVA',                   prefix: '',  suffix: '',  color: '#a855f7', info: 'Patient Visit Average — mean total visits per patient (all time)' },
  { key: 'cva',                  label: 'CVA',                   prefix: '$', suffix: '',  color: '#06b6d4', info: 'Cost Visit Average — average spend per visit' },
  { key: 'ltv',                  label: 'LTV',                   prefix: '$', suffix: '',  color: '#84cc16', info: 'Lifetime Value — CVA × PVA' },
  { key: 'cac',                  label: 'CAC',                   prefix: '$', suffix: '',  color: '#d946ef', info: 'Cost to Acquire a Patient — marketing spend ÷ new patients this month' },
];

function fmtStat(def, val) {
  if (val == null) return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const num = Number.isInteger(n) ? n : n.toFixed(1);
  return `${def.prefix}${num}${def.suffix}`;
}

// Traffic light thresholds — Jake to confirm exact values later
const THRESHOLDS = {
  tap_30d:              { green: 50,   orange: 20,    invert: false },
  avg_weekly_new:       { green: 5,    orange: 2,     invert: false },
  avg_weekly_appts:     { green: 60,   orange: 30,    invert: false },
  reactivations:        { green: 3,    orange: 1,     invert: false },
  attrition:              { green: 3,    orange: 8,     invert: true  }, // lower = better
  attrition_no_rebooking: { green: 5,    orange: 15,    invert: true  }, // lower = better
  keep_visit_rate:      { green: 85,   orange: 70,    invert: false },
  pva:                  { green: 30,   orange: 15,    invert: false },
  cva:                  { green: 80,   orange: 50,    invert: false },
  ltv:                  { green: 2500, orange: 1000,  invert: false },
  cac:                  { green: 200,  orange: 500,   invert: true  }, // lower = better
};

const LIGHT = { green: '#10b981', orange: '#f59e0b', red: '#ef4444' };

function getLight(key, val) {
  if (val == null) return null;
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  const t = THRESHOLDS[key];
  if (!t) return null;
  if (t.invert) {
    return n <= t.green ? 'green' : n <= t.orange ? 'orange' : 'red';
  }
  return n >= t.green ? 'green' : n >= t.orange ? 'orange' : 'red';
}

function RetentionFunnel({ snap, snapshots, selected, onToggle, t }) {
  const [view, setView] = useState('funnel');

  const stages = [
    { key: 'vc1',  label: 'Visit 1+',  color: '#6366f1' },
    { key: 'vc2',  label: 'Visit 2+',  color: '#8b5cf6' },
    { key: 'vc3',  label: 'Visit 3+',  color: '#a78bfa' },
    { key: 'vc6',  label: 'Visit 6+',  color: '#10b981' },
    { key: 'vc12', label: 'Visit 12+', color: '#00D4A0' },
    { key: 'vc24', label: 'Visit 24+', color: '#06b6d4' },
  ];
  const base = snap?.vc1 || 1;

  // Dropout bands — how many patients stopped in each phase
  const bands = [
    { label: '1 visit only',    sublabel: 'never rebooked',       count: (snap?.vc1||0) - (snap?.vc2||0), color: '#ef4444' },
    { label: '2–3 visits',      sublabel: 'dropped early',        count: (snap?.vc2||0) - (snap?.vc3||0), color: '#f97316' },
    { label: '3–6 visits',      sublabel: 'short care plan',      count: (snap?.vc3||0) - (snap?.vc6||0), color: '#f59e0b' },
    { label: '6–12 visits',     sublabel: 'mid care dropout',     count: (snap?.vc6||0) - (snap?.vc12||0), color: '#84cc16' },
    { label: '12–24 visits',    sublabel: 'extended care',        count: (snap?.vc12||0) - (snap?.vc24||0), color: '#10b981' },
    { label: '24+ visits',      sublabel: 'long-term patients',   count: snap?.vc24||0,                    color: '#00D4A0' },
  ];
  const maxBandCount = Math.max(...bands.map(b => b.count), 1);

  const tabBtn = active => ({
    padding: '5px 14px', borderRadius: 8, border: `1px solid ${active ? B.teal : t.border}`,
    background: active ? `${B.teal}18` : 'transparent',
    color: active ? B.teal : t.muted, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: B.sans,
  });

  return (
    <div>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={tabBtn(view === 'funnel')}  onClick={() => setView('funnel')}>Cumulative Funnel</button>
        <button style={tabBtn(view === 'dropout')} onClick={() => setView('dropout')}>Dropout Breakdown</button>
      </div>

      {view === 'funnel' && (
        <div>
          {stages.map(({ key, label, color }) => {
            const val = snap?.[key] ?? null;
            const pct = val != null && base > 0 ? (val / base * 100) : 0;
            const isSelected = selected.includes(key);
            return (
              <div key={key} onClick={() => onToggle(key)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, cursor: 'pointer' }}>
                <div style={{ width: 64, fontSize: 12, color: t.muted, textAlign: 'right', flexShrink: 0, fontWeight: 500 }}>{label}</div>
                <div style={{ flex: 1, background: t.bg, borderRadius: 5, height: 26, overflow: 'hidden',
                  outline: isSelected ? `2px solid ${color}` : 'none', outlineOffset: 1 }}>
                  <div style={{
                    width: val != null ? `${Math.max(pct, val > 0 ? 0.5 : 0)}%` : '0%',
                    height: '100%', background: color, borderRadius: 5, opacity: 0.75,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ width: 110, fontSize: 13, flexShrink: 0, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                  {val != null
                    ? <><span style={{ fontWeight: 700, color: t.textStrong, fontSize: 14 }}>{pct.toFixed(0)}%</span>
                        <span style={{ color: t.muted, fontSize: 11 }}>{val.toLocaleString()}</span></>
                    : <span style={{ color: t.muted }}>—</span>
                  }
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: t.muted, marginTop: 4, paddingLeft: 76 }}>
            % of patients who reached Visit 1+ &nbsp;·&nbsp; click a row to chart over time
          </div>
        </div>
      )}

      {view === 'dropout' && (
        <div>
          <div style={{ fontSize: 12, color: t.muted, marginBottom: 14 }}>
            Where patients stop coming — as a share of all patients who ever attended
          </div>
          {bands.map(({ label, sublabel, count, color }) => {
            const pct = base > 0 ? (count / base * 100) : 0;
            const barW = (count / maxBandCount) * 100;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 96, flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.textStrong }}>{label}</div>
                  <div style={{ fontSize: 10, color: t.muted }}>{sublabel}</div>
                </div>
                <div style={{ flex: 1, background: t.bg, borderRadius: 5, height: 28, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.max(barW, count > 0 ? 0.5 : 0)}%`,
                    height: '100%', background: color, borderRadius: 5, opacity: 0.8,
                    transition: 'width 0.4s ease',
                    display: 'flex', alignItems: 'center', paddingLeft: 8, boxSizing: 'border-box',
                  }}>
                    {barW > 15 && <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{pct.toFixed(0)}%</span>}
                  </div>
                </div>
                <div style={{ width: 80, fontSize: 13, flexShrink: 0, display: 'flex', gap: 5, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 700, color: t.textStrong }}>{count.toLocaleString()}</span>
                  {barW <= 15 && <span style={{ color: t.muted, fontSize: 11 }}>{pct.toFixed(0)}%</span>}
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: t.muted, marginTop: 4, paddingLeft: 108 }}>
            Total patients: {(snap?.vc1 || 0).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniSparkline({ data, dataKey, color }) {
  const vals = data.map(d => parseFloat(d[dataKey]) || 0).filter(v => v !== 0);
  if (vals.length < 2) return null;
  // 3-point trailing moving average
  const smoothed = vals.map((_, i) => {
    const sl = vals.slice(Math.max(0, i - 2), i + 1);
    return sl.reduce((a, b) => a + b, 0) / sl.length;
  });
  const min = Math.min(...smoothed), max = Math.max(...smoothed);
  const range = max - min || 1;
  const W = 120, H = 28;
  const pts = smoothed.map((v, i) => ({
    x: (i / (smoothed.length - 1)) * W,
    y: H - 4 - ((v - min) / range) * (H - 8),
  }));
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 28, marginTop: 10, display: 'block', opacity: 0.65 }}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Full interactive line chart for the expanded graph view
function LineChart({ series, data, t, height = 220, skipZeros = false }) {
  const [hovIdx, setHovIdx] = useState(null);
  if (!data || data.length < 2) return (
    <div style={{ textAlign: 'center', padding: '32px', color: t.muted, fontSize: 13 }}>
      No historical data yet — run a backfill to populate.
    </div>
  );

  // Drop months where every series value is 0 or missing
  const validData = data.filter(d => series.some(s => (parseFloat(d[s.key]) || 0) !== 0));
  if (validData.length < 2) return (
    <div style={{ textAlign: 'center', padding: '32px', color: t.muted, fontSize: 13 }}>
      No historical data yet — run a backfill to populate.
    </div>
  );

  const W = 820, H = height;
  const pad = { top: 16, right: 24, bottom: 44, left: 56 };
  const iW = W - pad.left - pad.right;
  const iH = H - pad.top - pad.bottom;

  // Smooth helper — 3-point trailing moving average
  const smoothArr = (arr, w = 3) => arr.map((_, i) => {
    const sl = arr.slice(Math.max(0, i - w + 1), i + 1);
    return sl.reduce((a, b) => a + b, 0) / sl.length;
  });

  const allVals = series.flatMap(s => validData.map(d => {
    const v = parseFloat(d[s.key]);
    return (skipZeros && (!v || v === 0)) ? null : (v || 0);
  }).filter(v => v !== null));
  if (allVals.length === 0) return <div style={{ textAlign: 'center', padding: '32px', color: t.muted, fontSize: 13 }}>No data yet.</div>;
  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const range = maxV - minV || 1;

  const xPos = i => pad.left + (i / Math.max(validData.length - 1, 1)) * iW;
  const yPos = v  => pad.top + iH - ((v - minV) / range) * iH;

  const yTickCount = 4;
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => minV + (range / yTickCount) * i);
  const xStep  = Math.max(1, Math.ceil(validData.length / 8));

  const hovX = hovIdx != null ? xPos(hovIdx) : null;
  const tipX = hovX != null ? (hovX + 14 + 180 > W ? hovX - 194 : hovX + 14) : 0;
  const tipH = 28 + series.length * 26;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block', overflow: 'visible' }}
      onMouseLeave={() => setHovIdx(null)}>

      {/* Grid lines + Y labels */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={pad.left} y1={yPos(v)} x2={pad.left + iW} y2={yPos(v)}
            stroke="rgba(148,163,184,0.08)" strokeWidth="1" />
          <text x={pad.left - 8} y={yPos(v) + 4} textAnchor="end"
            fill="rgba(148,163,184,0.45)" fontSize="10">
            {v % 1 === 0 ? Math.round(v) : v.toFixed(1)}
          </text>
        </g>
      ))}

      {/* X axis date labels */}
      {validData.map((d, i) => {
        if (i % xStep !== 0 && i !== validData.length - 1) return null;
        return (
          <text key={i} x={xPos(i)} y={H - 8} textAnchor="middle"
            fill="rgba(148,163,184,0.45)" fontSize="10">
            {new Date(d.week_start).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })}
          </text>
        );
      })}

      {/* Hover vertical rule */}
      {hovIdx != null && (
        <line x1={hovX} y1={pad.top} x2={hovX} y2={pad.top + iH}
          stroke="rgba(148,163,184,0.2)" strokeWidth="1" strokeDasharray="4,3" />
      )}

      {/* Series lines + hover dots */}
      {series.map(s => {
        const rawVals = validData.map(d => {
          const v = parseFloat(d[s.key]);
          return (skipZeros && (!v || v === 0)) ? null : (v || 0);
        });
        const smoothed = smoothArr(rawVals.map(v => v ?? 0));
        const pts = validData.map((_, i) => ({ x: xPos(i), y: yPos(smoothed[i]), v: rawVals[i] }));
        const pathD = pts.reduce((acc, p, i) => {
          if (p.v === null) return acc;
          const cmd = (i === 0 || pts[i - 1]?.v === null) ? 'M' : 'L';
          return acc + `${cmd}${p.x.toFixed(1)},${p.y.toFixed(1)} `;
        }, '').trim();
        return (
          <g key={s.key}>
            <path d={pathD} fill="none" stroke={s.color} strokeWidth="2.5"
              strokeLinejoin="round" strokeLinecap="round" />
            {hovIdx != null && pts[hovIdx] && pts[hovIdx].v !== null && (
              <circle cx={pts[hovIdx].x} cy={pts[hovIdx].y} r="5"
                fill={s.color} stroke="#0e1929" strokeWidth="2" />
            )}
          </g>
        );
      })}

      {/* Invisible hover hit areas */}
      {validData.map((_, i) => (
        <rect key={i}
          x={xPos(i) - iW / (2 * Math.max(validData.length - 1, 1))} y={pad.top}
          width={iW / Math.max(validData.length - 1, 1)} height={iH}
          fill="transparent" style={{ cursor: 'crosshair' }}
          onMouseEnter={() => setHovIdx(i)}
        />
      ))}

      {/* Tooltip */}
      {hovIdx != null && (
        <g transform={`translate(${tipX},${pad.top + 8})`}>
          <rect x="0" y="0" width="180" height={tipH} rx="8"
            fill="#0e1929" stroke="rgba(255,255,255,0.1)" strokeWidth="1"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }} />
          <text x="10" y="16" fill="rgba(148,163,184,0.55)" fontSize="10">
            {new Date(validData[hovIdx].week_start).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
          </text>
          {series.map((s, si) => {
            const v = parseFloat(validData[hovIdx][s.key]) || 0;
            const disp = `${s.prefix}${Number.isInteger(v) ? v : v.toFixed(1)}${s.suffix}`;
            return (
              <g key={s.key} transform={`translate(0,${28 + si * 26})`}>
                <rect x="10" y="-8" width="10" height="10" rx="3" fill={s.color} />
                <text x="26" y="1" fill="#f1f5f9" fontSize="13" fontWeight="700">{disp}</text>
                <text x="26" y="14" fill="rgba(148,163,184,0.5)" fontSize="10">{s.label}</text>
              </g>
            );
          })}
        </g>
      )}
    </svg>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────
function fmtN(val, { prefix = '', suffix = '', dec } = {}) {
  if (val == null) return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const s = dec != null ? n.toFixed(dec) : (Number.isInteger(n) ? String(n) : n.toFixed(1));
  return `${prefix}${s}${suffix}`;
}
function light(val, { green, orange, invert = false } = {}) {
  if (val == null || green == null) return null;
  const n = parseFloat(val); if (isNaN(n)) return null;
  if (invert) return n <= green ? 'green' : n <= orange ? 'orange' : 'red';
  return n >= green ? 'green' : n >= orange ? 'orange' : 'red';
}
const LC = { green: '#10b981', orange: '#f59e0b', red: '#ef4444' };

function LightDot({ color }) {
  if (!color) return null;
  return <div style={{ position: 'absolute', top: 10, right: 10, width: 9, height: 9, borderRadius: '50%', background: LC[color], boxShadow: `0 0 7px ${LC[color]}99` }} />;
}

function SectionCard({ title, children, chart, t }) {
  return (
    <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 16, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${t.border}` }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.muted }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
      {chart && (
        <div style={{ borderTop: `1px solid ${t.border}`, padding: '4px 12px 16px' }}>
          {chart}
        </div>
      )}
    </div>
  );
}

function StatTile({ dataKey, label, value, opts = {}, color, lightOpts, snapshots, selected, onToggle, t }) {
  const isOn = selected.includes(dataKey);
  const lc = light(value, lightOpts);
  return (
    <div onClick={() => onToggle(dataKey)} style={{
      background: isOn ? `${color}15` : t.card,
      border: `2px solid ${isOn ? color : t.border}`,
      borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
      userSelect: 'none', transition: 'all 0.15s', position: 'relative',
    }}>
      <LightDot color={lc} />
      <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color: isOn ? color : t.textStrong, fontFamily: B.serif }}>
        {fmtN(value, opts)}
      </div>
      <div style={{ fontSize: 10, fontWeight: 600, marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.06em', color: t.muted }}>
        {label}
      </div>
      {snapshots.length > 1 && <MiniSparkline data={snapshots} dataKey={dataKey} color={color} />}
    </div>
  );
}

// ─── Momentum Section ─────────────────────────────────────────────────────────
function MomentumCard({ label, value, newN, reactN, noFwdN, period, dataKey, snapshots, selected, onToggle, t }) {
  const isPos = value > 0, isNeg = value < 0;
  const c = isPos ? '#10b981' : isNeg ? '#ef4444' : '#f59e0b';
  const isOn = selected.includes(dataKey);
  const prefix = isPos ? '+' : '';
  return (
    <div onClick={() => onToggle(dataKey)} style={{
      flex: 1, minWidth: 260,
      background: isOn ? `${c}15` : t.card,
      border: `2px solid ${isOn ? c : c + '55'}`,
      borderRadius: 16, padding: '20px 24px', cursor: 'pointer',
      boxShadow: `0 0 20px ${c}22`, userSelect: 'none', transition: 'all 0.15s',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.muted, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 48, fontWeight: 900, lineHeight: 1, color: c, fontFamily: B.serif }}>
          {value != null ? `${prefix}${value}` : '—'}
        </span>
        <span style={{ fontSize: 13, color: t.muted }}>/ {period}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
        <div><span style={{ color: '#10b981', fontWeight: 700, marginRight: 8 }}>+{newN ?? 0}</span><span style={{ color: t.muted }}>new patients</span></div>
        <div><span style={{ color: '#6366f1', fontWeight: 700, marginRight: 8 }}>+{reactN ?? 0}</span><span style={{ color: t.muted }}>reactivations</span></div>
        <div><span style={{ color: '#ef4444', fontWeight: 700, marginRight: 8 }}>−{noFwdN ?? 0}</span><span style={{ color: t.muted }}>no forward appointment</span></div>
      </div>
      {snapshots.length > 1 && <div style={{ marginTop: 12 }}><MiniSparkline data={snapshots} dataKey={dataKey} color={c} /></div>}
    </div>
  );
}

// ─── BoltStats: main dashboard tab ────────────────────────────────────────────
function NetMomentumBanner({ liveStats, snapshots, t, toggle, selected }) {
  const nm = liveStats.net_momentum;
  const newWk    = liveStats.avg_weekly_new           ?? 0;
  const reactWk  = liveStats.avg_weekly_reactivations ?? 0;
  const attrWk   = liveStats.avg_weekly_attrition     ?? 0;

  const isPos  = nm > 0;
  const isNeg  = nm < 0;
  const color  = isPos ? '#10b981' : isNeg ? '#ef4444' : '#f59e0b';
  const glow   = isPos ? '#10b98133' : isNeg ? '#ef444433' : '#f59e0b33';
  const prefix = isPos ? '+' : '';
  const isOn   = selected.includes('net_momentum');

  const fmt = v => (v % 1 === 0 ? v : v.toFixed(1));

  return (
    <div
      onClick={() => toggle('net_momentum')}
      title="Net Momentum — click to see trend over time"
      style={{
        background: isOn ? `${color}18` : t.card,
        border: `2px solid ${isOn ? color : color + '55'}`,
        borderRadius: 16,
        padding: '20px 28px',
        marginBottom: 16,
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        userSelect: 'none',
        boxShadow: `0 0 24px ${glow}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
      }}>
      {/* Left: label + number */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.muted, marginBottom: 6 }}>
          Net Momentum
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 52, fontWeight: 900, lineHeight: 1, color, fontFamily: B.serif }}>
            {nm != null ? `${prefix}${fmt(nm)}` : '—'}
          </span>
          <span style={{ fontSize: 14, color: t.muted, fontWeight: 500 }}>patients / month</span>
        </div>
      </div>

      {/* Right: breakdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: t.muted, marginBottom: 2 }}>
          How it's calculated
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: '#10b981', fontWeight: 700, minWidth: 36 }}>+{fmt(newWk)}</span>
          <span style={{ color: t.muted }}>new patients this month</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: '#6366f1', fontWeight: 700, minWidth: 36 }}>+{fmt(reactWk)}</span>
          <span style={{ color: t.muted }}>reactivations this month</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: '#ef4444', fontWeight: 700, minWidth: 36 }}>−{fmt(attrWk)}</span>
          <span style={{ color: t.muted }}>cancelled / DNA, not rebooked</span>
        </div>
        <div style={{ height: 1, background: t.border, margin: '2px 0' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color, fontWeight: 800, minWidth: 36 }}>{nm != null ? `${prefix}${fmt(nm)}` : '—'}</span>
          <span style={{ color: t.muted }}>net momentum this month</span>
        </div>
      </div>

      {/* Sparkline */}
      {snapshots.length > 1 && (
        <div style={{ flex: '0 0 180px' }}>
          <MiniSparkline data={snapshots} dataKey="net_momentum" color={color} />
        </div>
      )}
    </div>
  );
}

// ─── Metrics Glossary ─────────────────────────────────────────────────────────
const GLOSSARY = [
  {
    key: 'net_momentum',
    label: 'Net Momentum',
    what: 'Whether the practice is growing, shrinking, or holding steady — expressed as a net weekly patient change.',
    how: 'Avg new patients/wk + avg reactivations/wk − avg patients lost/wk. Positive = growing, negative = shrinking.',
  },
  {
    key: 'tap_30d',
    label: 'Active Patients (TAP)',
    what: 'Total Active Patients — the number of unique patients who have attended at least one appointment in the last 30 days.',
    how: 'Count of distinct patients with a non-cancelled appointment in the rolling 30-day window.',
  },
  {
    key: 'avg_weekly_new',
    label: 'Avg New Patients / Wk',
    what: 'The average number of brand-new patients starting care each week — a key growth indicator.',
    how: 'Patients whose very first appointment fell in each week, averaged over the last 26 weeks.',
  },
  {
    key: 'avg_weekly_appts',
    label: 'Avg Appointments / Wk',
    what: 'The average total appointment volume per week — shows practice busyness and capacity utilisation.',
    how: 'Total non-cancelled appointments per week, averaged over the last 26 weeks.',
  },
  {
    key: 'reactivations',
    label: 'Reactivations',
    what: 'Patients who had gone quiet for 90+ days but now have a future appointment booked. Shows win-back success.',
    how: 'Count of patients whose last recorded visit was more than 90 days ago AND who have an upcoming appointment in the next 90 days.',
  },
  {
    key: 'attrition',
    label: 'Attrition (Cancelled/DNA)',
    what: 'Patients who actively disengaged this month — they cancelled or did not arrive for an appointment and have nothing booked going forward.',
    how: 'Count of patients with a cancelled or DNA appointment in the last 30 days AND zero upcoming appointments in the next 90 days.',
  },
  {
    key: 'attrition_no_rebooking',
    label: 'No Forward Booking',
    what: 'Patients who attended an appointment this month but have NOT booked their next one. This is your most actionable at-risk list — they\'re still engaged but haven\'t committed to returning.',
    how: 'Count of patients with a non-cancelled appointment in the last 30 days AND no future appointments booked beyond today.',
  },
  {
    key: 'keep_visit_rate',
    label: 'Keep Visit Rate',
    what: 'The percentage of appointments where the patient actually showed up vs. did not arrive. A proxy for commitment to care.',
    how: 'Attended ÷ (Attended + Did Not Arrive) × 100. Only counts appointments where Cliniko recorded an explicit status.',
  },
  {
    key: 'stick_rate',
    label: 'Stick Rate (IC → ROF)',
    what: 'Of all patients who had an Initial Consultation, what percentage proceeded to a Report of Findings appointment. Measures how well patients commit after their first visit.',
    how: 'Patients with both an IC and a ROF appointment ÷ all patients with an IC × 100.',
  },
  {
    key: 'yes_to_care_rate',
    label: 'Yes to Care (ROF → Adjustment)',
    what: 'Of patients who had a Report of Findings, what percentage then booked a Standard Adjustment — meaning they said yes to a care plan.',
    how: 'Patients who had a Standard Adjustment appointment AFTER their first ROF date ÷ all patients with a ROF × 100.',
  },
  {
    key: 'churn_pre_12',
    label: 'Churn (Pre-Visit 12)',
    what: 'Of patients who started in the last 12 months, the percentage who stopped before completing 12 visits. Early dropout is the biggest revenue leak in most practices.',
    how: 'Rolling 12-month cohort: patients with 1–11 visits, no upcoming appointments, last visit > 30 days ago ÷ all patients who started in the last 12 months × 100.',
  },
  {
    key: 'churn_pre_24',
    label: 'Churn (Visits 12–24)',
    what: 'Of patients who started in the last 12 months, the percentage who dropped between visits 12 and 24. Lower is better.',
    how: 'Rolling 12-month cohort: patients with 12–23 visits, no upcoming appointments, last visit > 30 days ago ÷ all patients who started in the last 12 months × 100.',
  },
  {
    key: 'r4_stick_rate',
    label: 'R4 Stick Rate',
    what: 'The percentage of patients who reached visit 24 — completing two full phases of care. Shows long-term retention.',
    how: 'Patients with 24 or more total visits ÷ all patients × 100.',
  },
  {
    key: 'r4_reconversion_rate',
    label: 'R4 Re-conversion Rate',
    what: 'Of patients who reached visit 24, what percentage recommitted to further care. Shows how well the practice converts long-term patients into ongoing wellness care.',
    how: 'Patients with 24+ visits AND an upcoming appointment ÷ all patients with 24+ visits × 100.',
  },
  {
    key: 'pva',
    label: 'PVA — Patient Visit Average',
    what: 'The average total number of visits per patient across all time. Higher means patients stay in care longer.',
    how: 'Sum of all non-cancelled visits ÷ number of patients.',
  },
  {
    key: 'cva',
    label: 'CVA — Cost Visit Average',
    what: 'Average revenue per visit. Measures how much the practice earns for each appointment.',
    how: 'Total invoiced amount per patient ÷ that patient\'s visit count, then averaged across all patients.',
  },
  {
    key: 'ltv',
    label: 'LTV — Lifetime Value',
    what: 'The estimated total revenue a patient generates over their entire time with the practice.',
    how: 'CVA × PVA. If a patient visits 30 times and each visit generates $95, their LTV = $2,850.',
  },
  {
    key: 'cac',
    label: 'CAC — Cost to Acquire a Patient',
    what: 'How much it costs in marketing spend to bring in one new patient. Lower is better.',
    how: 'Total marketing spend for the month ÷ number of new patients who started that month. Marketing spend is entered manually in the admin panel.',
  },
];

function MetricsGlossary({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: t.card, border: `1px solid ${t.border}`,
      borderRadius: 14, overflow: 'hidden', marginTop: 16,
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '16px 20px', cursor: 'pointer',
          borderBottom: open ? `1px solid ${t.border}` : 'none',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: t.textStrong, fontFamily: B.serif }}>
          How These Stats Are Calculated
        </span>
        <span style={{ fontSize: 12, color: t.muted }}>{open ? '▲ Collapse' : '▼ Expand'}</span>
      </div>
      {open && (
        <div style={{ padding: '8px 20px 20px' }}>
          {GLOSSARY.map(g => (
            <div key={g.key} style={{
              padding: '14px 0',
              borderBottom: `1px solid ${t.border}`,
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: t.textStrong, marginBottom: 4, fontFamily: B.serif }}>
                {g.label}
              </div>
              <div style={{ fontSize: 13, color: t.text, marginBottom: 6, lineHeight: 1.5 }}>
                {g.what}
              </div>
              <div style={{ fontSize: 12, color: t.muted, lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600, color: t.muted }}>Calculation: </span>{g.how}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BoltStats: main tab ──────────────────────────────────────────────────────
function StatsTab({ slug, pin, t }) {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [selected, setSelected]   = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);

  useEffect(() => {
    fetch(`/api/stats?slug=${encodeURIComponent(slug)}&pin=${encodeURIComponent(pin)}`)
      .then(r => r.json())
      .then(d => { if (d.error) { setError(d.error); return; } setStatsData(d); })
      .catch(() => setError('Could not load statistics'))
      .finally(() => setLoading(false));
  }, [slug, pin]);

  const toggle = key => setSelected(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );

  if (loading) return <div style={{ textAlign: 'center', padding: '60px 24px', color: t.muted, fontSize: 14 }}>Loading practice statistics…</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '60px 24px', color: B.red, fontSize: 13 }}>{error}</div>;

  const { liveStats: ls = {}, snapshots = [] } = statsData || {};

  const PULSE_SERIES = {
    tap_30d:             { label: 'Active Patients',      color: '#00D4A0' },
    new_7d:              { label: 'New Patients / Wk',    color: '#818cf8' },
    new_30d:             { label: 'New Patients / Mo',    color: '#6366f1' },
    appts_7d:            { label: 'Appts / Wk',           color: '#34d399' },
    appts_30d:           { label: 'Appts / Mo',           color: '#10b981' },
    avg_new_per_week:    { label: 'Avg New / Wk',         color: '#c4b5fd' },
    avg_new_per_month:   { label: 'Avg New / Mo',         color: '#a78bfa' },
    avg_react_per_week:  { label: 'Avg Reactivations/Wk', color: '#fdba74' },
    avg_react_per_month: { label: 'Avg Reactivations/Mo', color: '#fb923c' },
    no_fwd_30d:          { label: 'No Fwd Appt (30d)',    color: '#f87171' },
  };

  const tile = (key, label, value, color, opts, lightOpts) => (
    <StatTile key={key} dataKey={key} label={label} value={value} opts={opts}
      color={color} lightOpts={lightOpts} snapshots={snapshots}
      selected={selected} onToggle={toggle} t={t} />
  );

  const tdR = { padding: '9px 14px', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted, whiteSpace: 'nowrap' };
  const reportCols = [
    { key: 'cac',                    label: 'CAC ($)' },
    { key: 'thirty_dc',              label: '30DC ($)' },
    { key: 'ltv',                    label: 'LTV ($)' },
    { key: 'avg_monthly_collections',label: 'Revenue/Mo ($)' },
    { key: 'pva',                    label: 'PVA' },
    { key: 'momentum_30d',           label: 'Momentum/Mo' },
    { key: 'momentum_7d',            label: 'Momentum/Wk' },
    { key: 'tap_30d',                label: 'Active Pts' },
    { key: 'new_30d',                label: 'New/Mo' },
    { key: 'new_7d',                 label: 'New/Wk' },
    { key: 'appts_30d',              label: 'Appts/Mo' },
    { key: 'appts_7d',               label: 'Appts/Wk' },
    { key: 'avg_new_per_month',      label: 'Avg New/Mo' },
    { key: 'avg_new_per_week',       label: 'Avg New/Wk' },
    { key: 'vc1',                    label: '1+ Visits' },
    { key: 'vc2',                    label: '2+ Visits' },
    { key: 'vc3',                    label: '3+ Visits' },
    { key: 'vc6',                    label: '6+ Visits' },
    { key: 'vc12',                   label: '12+ Visits' },
    { key: 'vc24',                   label: '24+ Visits' },
    { key: 'cva',                    label: 'CVA ($)' },
  ];

  const fmt$ = v => v == null ? '—' : '$' + Math.round(v).toLocaleString('en-AU');

  return (
    <div>

      {/* ══ CORE 4 ═════════════════════════════════════════════════════════════ */}
      <style>{`@media(max-width:700px){.core4-grid{grid-template-columns:repeat(2,1fr)!important}}`}</style>
      <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 16, marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.muted }}>Core 4</span>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div className="core4-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { key: 'cac',               label: 'CAC',             sublabel: 'Cost per new patient',         formula: 'Marketing spend ÷ new patients',         bg: 'linear-gradient(135deg,#6366f1,#4f46e5)', value: fmt$(ls.cac),                empty: ls.cac == null },
              { key: 'thirty_dc',         label: '30DC',            sublabel: 'Revenue — first 30 days',      formula: 'Avg invoiced per patient in 30 days',    bg: 'linear-gradient(135deg,#0ea5e9,#0284c7)', value: fmt$(ls.thirty_dc),          empty: ls.thirty_dc == null },
              { key: 'ltv',               label: 'LTV',             sublabel: 'Lifetime patient value',       formula: 'CVA × PVA',                              bg: 'linear-gradient(135deg,#a855f7,#7c3aed)', value: fmt$(ls.ltv),                empty: ls.ltv == null },
              { key: 'revenue_this_month',label: 'Monthly Revenue', sublabel: new Date().toLocaleDateString('en-AU',{month:'long',year:'numeric'}), formula: 'Total invoiced this calendar month', bg: 'linear-gradient(135deg,#00D4A0,#00a87e)', value: fmt$(ls.revenue_this_month), empty: false },
              { key: 'pva',               label: 'PVA',             sublabel: 'Patient visit average',        formula: 'Avg visits per patient (all time)',      bg: 'linear-gradient(135deg,#f59e0b,#d97706)', value: ls.pva != null ? ls.pva.toFixed(1) : '—', empty: ls.pva == null },
            ].map(s => (
              <div key={s.key} style={{ background: s.bg, borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.empty ? 'rgba(255,255,255,0.3)' : '#fff', lineHeight: 1, fontFamily: B.serif }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 5 }}>{s.sublabel}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>{s.formula}</div>
              </div>
            ))}
          </div>
          <LineChart skipZeros height={180} data={snapshots} t={t} series={[
            { key: 'cac',                    label: 'CAC',             color: '#818cf8', prefix: '$', suffix: '' },
            { key: 'thirty_dc',              label: '30DC',            color: '#38bdf8', prefix: '$', suffix: '' },
            { key: 'ltv',                    label: 'LTV',             color: '#c084fc', prefix: '$', suffix: '' },
            { key: 'avg_monthly_collections',label: 'Monthly Revenue', color: '#00D4A0', prefix: '$', suffix: '' },
            { key: 'pva',                    label: 'PVA',             color: '#fbbf24', prefix: '',  suffix: ' visits' },
          ]} />
        </div>
      </div>

      {/* ══ Layer 1: Momentum ══════════════════════════════════════════════════ */}
      <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 16, marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.muted }}>Momentum</span>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <MomentumCard label="Weekly Momentum" dataKey="momentum_7d" value={ls.momentum_7d} period="week"
              newN={ls.new_7d} reactN={ls.reactivations_7d} noFwdN={ls.no_fwd_7d} snapshots={snapshots} selected={selected} onToggle={toggle} t={t} />
            <MomentumCard label="Monthly Momentum" dataKey="momentum_30d" value={ls.momentum_30d} period="month"
              newN={ls.new_30d} reactN={ls.reactivations_30d} noFwdN={ls.no_fwd_30d} snapshots={snapshots} selected={selected} onToggle={toggle} t={t} />
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${t.border}`, padding: '4px 12px 16px' }}>
          <LineChart height={200} data={snapshots} t={t} series={[
            { key: 'momentum_30d', label: 'Monthly Momentum', color: '#10b981', prefix: '', suffix: '' },
            { key: 'new_30d',      label: 'New Patients/Mo',  color: '#6366f1', prefix: '', suffix: '' },
            { key: 'reactivations_30d', label: 'Reactivations/Mo', color: '#f59e0b', prefix: '', suffix: '' },
            { key: 'no_fwd_30d',   label: 'No Fwd Appt (30d)', color: '#ef4444', prefix: '', suffix: '' },
          ]} />
        </div>
      </div>

      {/* ══ Layer 2: Practice Pulse ════════════════════════════════════════════ */}
      <SectionCard title="Practice Pulse" t={t}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
          {tile('tap_30d',           'Active Patients',     ls.tap_30d,           '#00D4A0', {}, { green: 80, orange: 40 })}
          {tile('new_7d',            'New Patients / Wk',   ls.new_7d,            '#818cf8', {}, { green: 5, orange: 2 })}
          {tile('new_30d',           'New Patients / Mo',   ls.new_30d,           '#6366f1', {}, { green: 15, orange: 6 })}
          {tile('appts_7d',          'Appts / Wk',          ls.appts_7d,          '#34d399', {}, { green: 50, orange: 25 })}
          {tile('appts_30d',         'Appts / Mo',          ls.appts_30d,         '#10b981', {}, { green: 160, orange: 80 })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
          {tile('avg_new_per_week',    'Avg New / Wk',        ls.avg_new_per_week,  '#c4b5fd', { dec: 1 }, { green: 4, orange: 2 })}
          {tile('avg_new_per_month',   'Avg New / Mo',        ls.avg_new_per_month, '#a78bfa', { dec: 1 }, { green: 16, orange: 8 })}
          {tile('avg_react_per_week',  'Avg Reactivations/Wk',ls.avg_react_per_week, '#fdba74', { dec: 1 }, { green: 2, orange: 0.5 })}
          {tile('avg_react_per_month', 'Avg Reactivations/Mo',ls.avg_react_per_month,'#fb923c', { dec: 1 }, { green: 6, orange: 2 })}
          {tile('no_fwd_30d',          'No Fwd Appt (30d)',   ls.no_fwd_30d,        '#f87171', {}, { green: 5, orange: 15, invert: true })}
        </div>
        {/* Click-to-overlay chart */}
        {(() => {
          const activeSeries = selected
            .filter(k => PULSE_SERIES[k])
            .map(k => ({ key: k, ...PULSE_SERIES[k], prefix: '', suffix: '' }));
          return (
            <div style={{ borderTop: `1px solid ${t.border}`, marginTop: 14, paddingTop: 8 }}>
              {activeSeries.length === 0
                ? <div style={{ textAlign: 'center', padding: '18px 0 6px', fontSize: 12, color: t.muted }}>
                    Tap any tile above to overlay its trend
                  </div>
                : <LineChart height={200} data={snapshots} t={t} series={activeSeries} />
              }
            </div>
          );
        })()}
      </SectionCard>

      {/* ══ Layer 3: Retention ════════════════════════════════════════════════ */}
      <SectionCard title="Retention Funnel" t={t}>
        <RetentionFunnel snap={ls} snapshots={snapshots} selected={selected} onToggle={toggle} t={t} />
      </SectionCard>

      {/* ══ Layer 4: Financial ════════════════════════════════════════════════ */}
      <SectionCard title="Financial Performance" t={t} chart={
        <LineChart height={200} data={snapshots} t={t} series={[
          { key: 'avg_weekly_collections', label: 'Avg Weekly Collections', color: '#10b981', prefix: '$', suffix: '' },
          { key: 'pva', label: 'Patient Visit Avg', color: '#a855f7', prefix: '',  suffix: '' },
          { key: 'cva', label: 'Cost per Visit',    color: '#06b6d4', prefix: '$', suffix: '' },
          { key: 'ltv', label: 'Lifetime Value',    color: '#84cc16', prefix: '$', suffix: '' },
        ]} />
      }>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {tile('avg_weekly_collections', 'Avg Weekly Collections', ls.avg_weekly_collections, '#10b981', { prefix: '$', dec: 0 }, { green: 5000, orange: 2500 })}
          {tile('pva', 'Patient Visit Average', ls.pva, '#a855f7', { dec: 1 }, { green: 30, orange: 15 })}
          {tile('cva', 'Cost per Visit (CVA)',  ls.cva, '#06b6d4', { prefix: '$', dec: 2 }, { green: 90, orange: 60 })}
          {tile('ltv', 'Lifetime Value (LTV)',  ls.ltv, '#84cc16', { prefix: '$', dec: 0 }, { green: 2500, orange: 1000 })}
        </div>
      </SectionCard>

      {/* ══ Monthly Reports ═══════════════════════════════════════════════════ */}
      <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}>
        <div onClick={() => setShowReports(r => !r)} style={{ padding: '14px 20px', cursor: 'pointer', borderBottom: showReports ? `1px solid ${t.border}` : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: t.textStrong, fontFamily: B.serif }}>Monthly Reports</span>
          <span style={{ fontSize: 12, color: t.muted }}>{showReports ? '▲ Collapse' : '▼ Expand'}</span>
        </div>
        {showReports && (
          snapshots.length === 0
            ? <div style={{ padding: '32px', textAlign: 'center', color: t.muted, fontSize: 13 }}>No reports yet — run a backfill in the admin panel.</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
                  <thead><tr>
                    <th style={{ ...tdR, fontWeight: 700, color: t.muted, background: t.bg, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Month</th>
                    {reportCols.map(c => <th key={c.key} style={{ ...tdR, fontWeight: 700, color: t.muted, background: t.bg, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</th>)}
                  </tr></thead>
                  <tbody>
                    {Object.values(
                      snapshots.reduce((acc, s) => {
                        const key = new Date(s.week_start).toISOString().slice(0, 7);
                        acc[key] = s; // snapshots are ASC — last entry per month wins (most complete)
                        return acc;
                      }, {})
                    ).reverse().map((s, i) => (
                      <tr key={i}>
                        <td style={{ ...tdR, fontWeight: 600, color: t.textStrong }}>{new Date(s.week_start).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}</td>
                        {reportCols.map(c => {
                          const d = DEFS[c.key] || {};
                          return <td key={c.key} style={{ ...tdR, color: t.text }}>{fmtN(s[c.key], d)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}
      </div>

      {/* ══ Glossary ══════════════════════════════════════════════════════════ */}
      <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div onClick={() => setShowGlossary(g => !g)} style={{ padding: '14px 20px', cursor: 'pointer', borderBottom: showGlossary ? `1px solid ${t.border}` : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: t.textStrong, fontFamily: B.serif }}>How These Stats Are Calculated</span>
          <span style={{ fontSize: 12, color: t.muted }}>{showGlossary ? '▲ Collapse' : '▼ Expand'}</span>
        </div>
        {showGlossary && (
          <div style={{ padding: '8px 20px 20px' }}>
            {[
              { label: 'Momentum (Weekly / Monthly)', what: 'Whether the practice is gaining or losing patients in a given period.', how: 'New patients + Reactivations (broke 90-day absence) − Patients with no forward appointment. Calculated separately for 7-day (weekly) and 30-day (monthly) windows.' },
              { label: 'Active Patients', what: 'Unique patients with a non-cancelled appointment in the last 30 days.', how: 'Distinct count of patients in appointment_map where appointment_date > 30 days ago and status not cancelled.' },
              { label: 'New Patients (7d / 30d)', what: 'Patients whose very first ever appointment was in the last 7 or 30 days.', how: 'Patients grouped by first appointment date, filtered to the respective window.' },
              { label: 'Appointments (7d / 30d)', what: 'Total non-cancelled appointments in the last 7 or 30 days.', how: 'Count of appointment_map rows where status not cancelled and appointment_date in window.' },
              { label: 'Avg New / Wk and Avg New / Mo', what: 'Rolling average of new patient starts over the last 12 months.', how: 'New patients per month for the last 12 months averaged, then divided by 4.33 for the weekly figure.' },
              { label: 'No Forward Appointment', what: 'Patients who attended in the last 30 days but have nothing booked after today — your most actionable at-risk list.', how: 'Active patients in the window who have zero appointments with a future date.' },
              { label: 'Stick Rate (IC → ROF)', what: 'Of patients who had an Initial Consultation, what % also had a Report of Findings.', how: 'Patients with both IC-type and ROF-type appointments ÷ patients with IC × 100.' },
              { label: 'ROF → Forward Appointment', what: 'Of patients who had a ROF in the last 12 months, what % currently have a future appointment booked — they committed to a care plan.', how: 'ROF patients (last 12 months) with at least one future non-cancelled appointment ÷ total ROF patients × 100.' },
              { label: 'Churn (1–12 / 12–24 / 24+)', what: 'Percentage of patients who dropped off at each phase of their care journey. Lower is better. Measured on the rolling 12-month cohort.', how: 'Patients whose first appointment was in the last 12 months, grouped by visit count phase, where last visit > 30 days ago and no upcoming appointments.' },
              { label: 'PVA — Patient Visit Average', what: 'Mean number of visits per patient across all time.', how: 'Total non-cancelled visits ÷ number of patients.' },
              { label: 'CVA — Cost Visit Average', what: 'Average revenue generated per visit.', how: 'Total invoiced amount per patient ÷ visit count, averaged across all patients.' },
              { label: 'LTV — Lifetime Value', what: 'Estimated total revenue per patient.', how: 'CVA × PVA.' },
            ].map(g => (
              <div key={g.label} style={{ padding: '12px 0', borderBottom: `1px solid ${t.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: t.textStrong, marginBottom: 3, fontFamily: B.serif }}>{g.label}</div>
                <div style={{ fontSize: 13, color: t.text, marginBottom: 4, lineHeight: 1.5 }}>{g.what}</div>
                <div style={{ fontSize: 12, color: t.muted, lineHeight: 1.5 }}><span style={{ fontWeight: 600 }}>Calculation: </span>{g.how}</div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

// ─── Progress Reports — SVG Charts ────────────────────────────────────────────
function RadarChart({ before, after, labels }) {
  const cx = 155, cy = 155, r = 110, n = labels.length;
  const ang = i => (2 * Math.PI * i / n) - Math.PI / 2;
  const pt  = (val, i) => {
    const a = ang(i), d = (Math.max(0, val) / 100) * r;
    return [cx + d * Math.cos(a), cy + d * Math.sin(a)];
  };
  const bPts = before.map((v, i) => pt(v, i).join(',')).join(' ');
  const aPts = after.map((v, i) => pt(v, i).join(',')).join(' ');
  const shortLabel = l => l.split(' / ')[0].replace(' Pain', '').replace('Headaches', 'Headaches');

  return (
    <svg viewBox="0 0 310 310" width="100%" style={{ maxWidth: 310 }}>
      {[25, 50, 75, 100].map(pct => (
        <circle key={pct} cx={cx} cy={cy} r={r * pct / 100} fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
      ))}
      {labels.map((_, i) => {
        const [x, y] = pt(100, i);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="currentColor" strokeWidth="0.5" opacity="0.15" />;
      })}
      <polygon points={bPts} fill="rgba(148,163,184,0.25)" stroke="#94a3b8" strokeWidth="1.5" />
      <polygon points={aPts} fill="rgba(0,212,160,0.2)"  stroke="#00D4A0" strokeWidth="2" />
      {labels.map((label, i) => {
        const a = ang(i), d = r + 20;
        const lx = cx + d * Math.cos(a), ly = cy + d * Math.sin(a);
        return (
          <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
            fontSize="9" fill="currentColor" opacity="0.6" fontFamily="system-ui,sans-serif">
            {shortLabel(label)}
          </text>
        );
      })}
    </svg>
  );
}

function TrendChart({ surveys }) {
  if (surveys.length < 2) return null;
  const w = 400, h = 120, pl = 36, pr = 16, pt = 18, pb = 28;
  const iw = w - pl - pr, ih = h - pt - pb;
  const scores = surveys.map(s => s.composite);
  const lo = Math.max(0, Math.min(...scores) - 10);
  const hi = Math.min(100, Math.max(...scores) + 10);
  const rng = hi - lo || 1;
  const sx = i => pl + (i / (surveys.length - 1)) * iw;
  const sy = v => pt + ih - ((v - lo) / rng) * ih;

  const pathD = surveys.map((s, i) => `${i === 0 ? 'M' : 'L'} ${sx(i)} ${sy(s.composite)}`).join(' ');
  const areaD = `${pathD} L ${sx(surveys.length - 1)} ${pt + ih} L ${sx(0)} ${pt + ih} Z`;

  const fmt = d => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt) ? '' : dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id="trendGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00D4A0" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#00D4A0" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[lo, (lo + hi) / 2, hi].map(v => (
        <line key={v} x1={pl} y1={sy(v)} x2={w - pr} y2={sy(v)}
          stroke="currentColor" strokeWidth="0.5" strokeDasharray="4,4" opacity="0.12" />
      ))}
      <path d={areaD} fill="url(#trendGrad)" />
      <path d={pathD} fill="none" stroke="#00D4A0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {surveys.map((s, i) => (
        <g key={i}>
          <circle cx={sx(i)} cy={sy(s.composite)} r="4.5" fill="#00D4A0" />
          <text x={sx(i)} y={sy(s.composite) - 10} textAnchor="middle" fontSize="10" fill="#00a87e" fontFamily="system-ui" fontWeight="700">
            {Math.round(s.composite)}
          </text>
          <text x={sx(i)} y={h - 4} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.5" fontFamily="system-ui">
            {fmt(s.date) || `S${s.index}`}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ─── Progress Reports Section ─────────────────────────────────────────────────
const CAT_KEYS   = ['pain', 'mood', 'sleep', 'energy', 'health'];
const CAT_LABELS = ['Pain', 'Mood & Stress', 'Sleep', 'Energy', 'Health'];
const CAT_COLORS = ['#ef4444', '#8b5cf6', '#f59e0b', '#3b82f6', '#10b981'];

function ProgressReportsSection({ clients, t }) {
  const [clientId, setClientId]           = React.useState('');
  const [search, setSearch]               = React.useState('');
  const [contacts, setContacts]           = React.useState([]);
  const [hasSearched, setHasSearched]     = React.useState(false);
  const [searching, setSearching]         = React.useState(false);
  const [selected, setSelected]           = React.useState(null);
  const [reportData, setReportData]       = React.useState(null);
  const [loadingReport, setLoadingReport] = React.useState(false);
  const [beforeIdx, setBeforeIdx]         = React.useState(0);
  const [afterIdx, setAfterIdx]           = React.useState(null);
  const [copied, setCopied]               = React.useState(null);
  const [backfilling, setBackfilling]     = React.useState(false);
  const [backfillMsg, setBackfillMsg]     = React.useState('');

  const handleBackfillNames = async () => {
    if (!clientId) return;
    setBackfilling(true); setBackfillMsg('');
    try {
      const res = await api.backfillSurveyNames(clientId);
      setBackfillMsg(res.message || 'Done');
      // Refresh search results so names appear
      if (hasSearched) handleSearch();
    } catch (err) {
      setBackfillMsg('Error: ' + err.message);
    } finally {
      setBackfilling(false);
    }
  };

  const card  = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, boxShadow: t.shadow };
  const inp   = { padding: '9px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.bg, color: t.textStrong, fontSize: 14, fontFamily: B.sans, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const btn   = { padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: B.teal, color: B.navy, fontFamily: B.sans };
  const ghost = { padding: '7px 14px', borderRadius: 8, border: `1px solid ${t.border}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: 'transparent', color: t.muted, fontFamily: B.sans };

  const handleSearch = async () => {
    if (!clientId) return;
    setSearching(true); setHasSearched(true);
    try {
      const res = await api.searchProgress(clientId, search);
      setContacts(res.contacts || []);
    } catch { setContacts([]); }
    finally { setSearching(false); }
  };

  const handleSelectContact = async (contact) => {
    setSelected(contact); setReportData(null); setBeforeIdx(0); setAfterIdx(null); setLoadingReport(true);
    try {
      const res = await api.getSurveyResults(contact.id, clientId);
      setReportData(res);
    } finally { setLoadingReport(false); }
  };

  const copySurveyLink = (contactId) => {
    const url = `${window.location.origin}/api/survey?client_id=${clientId}&contact_id=${contactId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(contactId);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const surveys       = reportData?.surveys || [];
  const ql            = reportData?.questionLabels || [];
  const beforeSurvey  = surveys[beforeIdx] || surveys[0];
  const afterSurvey   = surveys[afterIdx !== null ? afterIdx : surveys.length - 1] || surveys[surveys.length - 1];
  const improvement   = beforeSurvey && afterSurvey
    ? Math.round(((afterSurvey.composite - beforeSurvey.composite) / Math.max(beforeSurvey.composite, 1)) * 100)
    : 0;

  const fmtDate = d => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Category radar data (5 spokes, 0-100 scale)
  const catBefore = CAT_KEYS.map(k => beforeSurvey?.categories?.[k] || 0);
  const catAfter  = CAT_KEYS.map(k => afterSurvey?.categories?.[k] || 0);

  // Individual question rows (raw 0-10 scores, normalized to 0-100 for display)
  const questionRows = ql.map((label, i) => {
    const bRaw = beforeSurvey?.questions?.[i] ?? 0;
    const aRaw = afterSurvey?.questions?.[i] ?? 0;
    // Questions where higher raw = worse: 1-6, 12 (0-indexed: 0-5, 11)
    const invert = [0,1,2,3,4,5,11].includes(i);
    const b = invert ? (10 - bRaw) * 10 : bRaw * 10;
    const a = invert ? (10 - aRaw) * 10 : aRaw * 10;
    const pct = b > 0 ? Math.round(((a - b) / b) * 100) : 0;
    return { label, b, a, pct };
  });

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

      {/* ── Left: search panel ── */}
      <div style={{ ...card, padding: '20px', width: 300, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.textStrong, fontFamily: B.serif, marginBottom: 16 }}>Find a patient</div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: t.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Clinic</div>
          <select style={inp} value={clientId} onChange={e => { setClientId(e.target.value); setContacts([]); setHasSearched(false); setSelected(null); setReportData(null); }}>
            <option value="">Select clinic…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: t.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Patient name</div>
          <input style={inp} placeholder="Search by name…" value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()} />
        </div>
        <button style={{ ...btn, width: '100%', marginBottom: 8 }} onClick={handleSearch} disabled={!clientId || searching}>
          {searching ? 'Searching…' : 'Search'}
        </button>

        {clientId && (
          <div style={{ marginBottom: 14 }}>
            <button
              style={{ ...ghost, width: '100%', fontSize: 12 }}
              onClick={handleBackfillNames}
              disabled={backfilling}
            >
              {backfilling ? 'Fixing names…' : '🔧 Fix missing patient names'}
            </button>
            {backfillMsg && (
              <div style={{ fontSize: 12, color: '#00a87e', marginTop: 4, textAlign: 'center' }}>{backfillMsg}</div>
            )}
          </div>
        )}

        {hasSearched && (
          contacts.length === 0 ? (
            <div style={{ fontSize: 13, color: t.muted, textAlign: 'center', padding: '12px 0' }}>No patients found</div>
          ) : (
            contacts.map(c => (
              <div key={c.id} style={{ marginBottom: 8 }}>
                <div onClick={() => handleSelectContact(c)} style={{
                  padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                  background: selected?.id === c.id ? (B.teal + '18') : t.bg,
                  border: `1px solid ${selected?.id === c.id ? B.teal : t.border}`,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: t.textStrong }}>{c.firstName} {c.lastName}</div>
                  <div style={{ fontSize: 12, color: t.muted, marginTop: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {c.surveyCount > 0
                      ? <span style={{ color: '#00a87e', fontWeight: 700 }}>{c.surveyCount} survey{c.surveyCount !== 1 ? 's' : ''} done</span>
                      : <span>No surveys yet</span>}
                  </div>
                </div>
                {clientId && (
                  <button onClick={() => copySurveyLink(c.id)} style={{
                    ...ghost, width: '100%', fontSize: 11, marginTop: 3, padding: '5px 10px',
                    color: copied === c.id ? '#00a87e' : t.muted,
                    borderColor: copied === c.id ? '#00a87e' : t.border,
                  }}>
                    {copied === c.id ? '✓ Copied!' : '🔗 Copy survey link'}
                  </button>
                )}
              </div>
            ))
          )
        )}
      </div>

      {/* ── Right: report panel ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected && (
          <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }}>📊</div>
            <div style={{ fontWeight: 700, fontFamily: B.serif, color: t.textStrong, marginBottom: 4 }}>Select a patient</div>
            <div style={{ fontSize: 13, color: t.muted }}>Search for a patient on the left to view their progress report.</div>
          </div>
        )}

        {selected && loadingReport && (
          <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: t.muted }}>Loading…</div>
          </div>
        )}

        {selected && !loadingReport && reportData && surveys.length < 2 && (
          <div style={{ ...card, padding: '40px 28px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 700, fontFamily: B.serif, color: t.textStrong, fontSize: 18, marginBottom: 8 }}>
              {selected.firstName} has {surveys.length === 1 ? '1 survey completed' : 'no surveys yet'}
            </div>
            <div style={{ fontSize: 14, color: t.muted, marginBottom: 20 }}>
              {surveys.length === 1
                ? 'A progress comparison needs at least 2 completed surveys.'
                : 'Send this patient a survey link to get started.'}
            </div>
            {clientId && (
              <button onClick={() => copySurveyLink(selected.id)} style={{ ...btn, padding: '10px 20px' }}>
                {copied === selected.id ? '✓ Copied!' : '🔗 Copy survey link'}
              </button>
            )}
          </div>
        )}

        {selected && !loadingReport && reportData && surveys.length >= 2 && (
          <div>
            {/* Header */}
            <div style={{ ...card, padding: '20px 24px', marginBottom: 14, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: '#00a87e', marginBottom: 4 }}>Progress Report</div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: B.serif, color: t.textStrong, letterSpacing: '-0.5px' }}>
                  {selected.firstName} {selected.lastName}
                </div>
                <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>{surveys.length} surveys completed</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {clientId && (
                  <button style={{ ...ghost, fontSize: 12 }} onClick={() => copySurveyLink(selected.id)}>
                    {copied === selected.id ? '✓ Copied!' : '🔗 Send next survey'}
                  </button>
                )}
                <button style={{ ...btn, fontSize: 12 }} onClick={() => window.open(`/api/progress-print?client_id=${clientId}&contact_id=${selected.id}`, '_blank')}>
                  Print / PDF ↗
                </button>
              </div>
            </div>

            {/* Survey selector (3+ surveys) */}
            {surveys.length > 2 && (
              <div style={{ ...card, padding: '14px 18px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: t.muted, marginBottom: 10 }}>Compare surveys</div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {[{ label: 'Baseline', idx: beforeIdx, set: setBeforeIdx }, { label: 'Compare to', idx: afterIdx !== null ? afterIdx : surveys.length - 1, set: i => setAfterIdx(i) }].map(({ label, idx, set }) => (
                    <div key={label}>
                      <div style={{ fontSize: 11, color: t.muted, marginBottom: 6 }}>{label}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {surveys.map((s, i) => (
                          <button key={i} style={{
                            padding: '4px 12px', borderRadius: 100, border: `1.5px solid ${idx === i ? B.teal : t.border}`,
                            background: idx === i ? (B.teal + '18') : 'transparent',
                            color: idx === i ? B.teal : t.muted, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: B.sans,
                          }} onClick={() => set(i)}>
                            #{s.index}{s.date ? ` · ${fmtDate(s.date)}` : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Score cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div style={{ ...card, padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: t.muted, marginBottom: 4 }}>Before</div>
                {beforeSurvey.date && <div style={{ fontSize: 11, color: t.muted, marginBottom: 4 }}>{fmtDate(beforeSurvey.date)}</div>}
                <div style={{ fontSize: 42, fontWeight: 900, fontFamily: B.serif, color: t.textStrong, lineHeight: 1 }}>
                  {Math.round(beforeSurvey.composite)}<span style={{ fontSize: 16, fontWeight: 500, opacity: 0.4 }}>/100</span>
                </div>
              </div>
              <div style={{ background: 'linear-gradient(135deg,#00D4A0,#00a87e)', borderRadius: 14, padding: '18px', textAlign: 'center', boxShadow: t.shadow }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>Now</div>
                {afterSurvey.date && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginBottom: 4 }}>{fmtDate(afterSurvey.date)}</div>}
                <div style={{ fontSize: 42, fontWeight: 900, fontFamily: B.serif, color: '#fff', lineHeight: 1 }}>
                  {Math.round(afterSurvey.composite)}<span style={{ fontSize: 16, fontWeight: 500, opacity: 0.7 }}>/100</span>
                </div>
              </div>
              <div style={{ background: improvement >= 0 ? '#0a1f14' : '#1a0a0a', borderRadius: 14, padding: '18px', textAlign: 'center', boxShadow: t.shadow }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Change</div>
                <div style={{ fontSize: 42, fontWeight: 900, fontFamily: B.serif, color: improvement >= 0 ? '#00D4A0' : '#f87171', lineHeight: 1 }}>
                  {improvement >= 0 ? '+' : ''}{improvement}<span style={{ fontSize: 16, fontWeight: 500, opacity: 0.7 }}>%</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>Overall</div>
              </div>
            </div>

            {/* Charts row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div style={{ ...card, padding: '16px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: t.muted, marginBottom: 8 }}>Health profile</div>
                <div style={{ display: 'flex', justifyContent: 'center', color: t.text }}>
                  <RadarChart before={catBefore} after={catAfter} labels={CAT_LABELS} />
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  {[{ c: '#94a3b8', l: 'Before' }, { c: '#00D4A0', l: 'Now' }].map(({ c, l }) => (
                    <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: t.muted }}>
                      <span style={{ width: 10, height: 3, background: c, display: 'inline-block', borderRadius: 2 }}></span>{l}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ ...card, padding: '16px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: t.muted, marginBottom: 8 }}>Score over time</div>
                <div style={{ color: t.text }}><TrendChart surveys={surveys} /></div>
              </div>
            </div>

            {/* Category breakdown */}
            <div style={{ ...card, padding: '18px 20px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: t.muted, marginBottom: 14 }}>Category breakdown</div>
              {CAT_KEYS.map((key, i) => {
                const b = beforeSurvey?.categories?.[key] || 0;
                const a = afterSurvey?.categories?.[key] || 0;
                const pct = b > 0 ? Math.round(((a - b) / b) * 100) : 0;
                return (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: CAT_COLORS[i], display: 'inline-block' }}></span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: t.textStrong }}>{CAT_LABELS[i]}</span>
                      </div>
                      <div style={{
                        fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 100,
                        background: pct > 0 ? '#e0faf4' : pct < 0 ? 'rgba(239,68,68,0.1)' : t.bg,
                        color: pct > 0 ? '#00a87e' : pct < 0 ? '#ef4444' : t.muted,
                      }}>{pct > 0 ? '↑ +' : pct < 0 ? '↓ ' : ''}{pct}%</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {[{ label: 'Before', val: b, clr: '#94a3b8' }, { label: 'Now', val: a, clr: CAT_COLORS[i] }].map(bar => (
                        <div key={bar.label}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: t.muted }}>{bar.label}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: t.textStrong }}>{Math.round(bar.val)}/100</div>
                          </div>
                          <div style={{ background: t.bg, borderRadius: 4, height: 8, overflow: 'hidden' }}>
                            <div style={{ width: `${bar.val}%`, height: '100%', background: bar.clr, borderRadius: 4, transition: 'width .4s ease' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Question detail */}
            <details style={{ ...card, padding: '18px 20px' }}>
              <summary style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: t.muted, cursor: 'pointer', userSelect: 'none' }}>
                Question-by-question detail ▾
              </summary>
              <div style={{ marginTop: 14 }}>
                {questionRows.map((r, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: t.textStrong }}>Q{i+1}: {r.label}</div>
                      <div style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 100,
                        background: r.pct > 0 ? '#e0faf4' : r.pct < 0 ? 'rgba(239,68,68,0.1)' : t.bg,
                        color: r.pct > 0 ? '#00a87e' : r.pct < 0 ? '#ef4444' : t.muted,
                      }}>{r.pct > 0 ? '↑ +' : r.pct < 0 ? '↓ ' : ''}{r.pct}%</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {[{ label: 'Before', val: r.b }, { label: 'Now', val: r.a, teal: true }].map(bar => (
                        <div key={bar.label}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: t.muted }}>{bar.label}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: t.textStrong }}>{bar.val}/100</div>
                          </div>
                          <div style={{ background: t.bg, borderRadius: 3, height: 6, overflow: 'hidden' }}>
                            <div style={{ width: `${bar.val}%`, height: '100%', background: bar.teal ? '#00D4A0' : '#94a3b8', borderRadius: 3, transition: 'width .4s ease' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>

          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mirror Health tab ────────────────────────────────────────────────────────
function MirrorHealthSection({ t }) {
  const [status, setStatus]       = React.useState(null);
  const [loading, setLoading]     = React.useState(true);
  const [error, setError]         = React.useState(null);
  const [showAdvanced, setShowAdv] = React.useState(false);
  const [driftReport, setDrift]   = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.getMirrorStatus();
      setStatus(r);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  const loadDrift = React.useCallback(async () => {
    try { setDrift(await api.getDriftReport(24)); } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);
  React.useEffect(() => { if (showAdvanced) loadDrift(); }, [showAdvanced, loadDrift]);

  const card = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, boxShadow: t.shadow, padding: 20, marginBottom: 16 };
  const th_  = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${t.border}` };
  const td_  = { padding: '10px 12px', fontSize: 13, color: t.textStrong, borderBottom: `1px solid ${t.border}` };

  if (loading && !status) return <div style={{ padding: 40, textAlign: 'center', color: t.muted }}>Loading mirror status… (this checks Cliniko so it takes a few seconds)</div>;
  if (error)              return <div style={{ padding: 20, color: B.red }}>Error: {error}</div>;
  if (!status)            return null;

  const fmtAppt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div>
      {/* Refresh button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={load} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.muted, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
          ↻ Refresh
        </button>
      </div>

      {/* Per-client cards */}
      {(status.clients || []).map((c) => {
        const stale = c.lastCronAt && (Date.now() - new Date(c.lastCronAt).getTime()) > 15 * 60 * 1000;
        const healthy = c.appointments.missing === 0;
        const headerBg = healthy
          ? 'linear-gradient(135deg, #00D4A0, #00a87e)'
          : 'linear-gradient(135deg, #f59e0b, #b45309)';
        return (
          <div key={c.clientId} style={card}>
            {/* Header */}
            <div style={{ background: headerBg, borderRadius: 10, padding: '14px 18px', marginBottom: 16, color: '#fff' }}>
              <div style={{ fontSize: 18, fontWeight: 800, fontFamily: B.serif }}>{c.clientName}</div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                Last sync: {c.lastCronAt ? new Date(c.lastCronAt).toLocaleString() : 'never'}
                {stale && <span style={{ marginLeft: 8, background: 'rgba(0,0,0,0.25)', padding: '2px 8px', borderRadius: 12, fontSize: 10 }}>STALE</span>}
              </div>
            </div>

            {/* Big summary numbers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div style={{ padding: 16, border: `1px solid ${t.border}`, borderRadius: 10, background: t.bg }}>
                <div style={{ fontSize: 11, color: t.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Appointments</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: t.textStrong, fontFamily: B.serif, marginTop: 4 }}>
                  {c.appointments.mirrored} <span style={{ color: t.muted, fontSize: 18 }}>/ {c.appointments.clinikoTotal}</span>
                </div>
                <div style={{ fontSize: 13, color: healthy ? B.teal : B.amber, fontWeight: 600, marginTop: 4 }}>
                  {healthy
                    ? `✓ All synced (${c.appointments.percentage}%)`
                    : `${c.appointments.missing} missing (${c.appointments.percentage}%)`}
                </div>
              </div>

              <div style={{ padding: 16, border: `1px solid ${t.border}`, borderRadius: 10, background: t.bg }}>
                <div style={{ fontSize: 11, color: t.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Unavailable Blocks</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: t.textStrong, fontFamily: B.serif, marginTop: 4 }}>
                  {c.blocks.mirrored}
                </div>
                <div style={{ fontSize: 13, color: t.muted, fontWeight: 600, marginTop: 4 }}>
                  mirrored to GHL
                </div>
              </div>
            </div>

            {/* Missing list — only if non-empty */}
            {c.appointments.missing > 0 && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textStrong, fontFamily: B.serif, marginBottom: 8 }}>
                  Appointments not yet in GHL
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['Patient', 'Appointment', 'Type', 'Why'].map(h => <th key={h} style={th_}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {c.appointments.missingList.map((m, i) => (
                        <tr key={i}>
                          <td style={td_}><strong>{m.patientName}</strong></td>
                          <td style={td_}>{fmtAppt(m.startTime)}</td>
                          <td style={td_}>{m.typeName || '—'}</td>
                          <td style={{ ...td_, fontSize: 12, color: t.muted }}>{m.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {c.appointments.missingList.length < c.appointments.missing && (
                  <div style={{ fontSize: 12, color: t.muted, marginTop: 8 }}>
                    Showing first {c.appointments.missingList.length} of {c.appointments.missing}. Remaining will appear here as they're processed.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Advanced toggle */}
      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowAdv(!showAdvanced)} style={{
          padding: '8px 16px', borderRadius: 8, border: `1px solid ${t.border}`,
          background: 'transparent', color: t.muted, fontSize: 12, cursor: 'pointer', fontWeight: 600,
        }}>
          {showAdvanced ? '▼' : '▶'} Technical details (for debugging)
        </button>
      </div>

      {/* Advanced: raw drift log */}
      {showAdvanced && driftReport && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={{ fontSize: 13, color: t.muted, marginBottom: 12 }}>
            Raw sync events from the last 24h. Most of this is housekeeping (orphan cleanup, etc). The main thing to watch for: <strong style={{ color: B.red }}>unauthorised-cancel-blocked</strong> (the safety net stopped a bad Cliniko delete) or anything ending in <strong style={{ color: B.red }}>-error</strong>.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 12 }}>
            {Object.entries(driftReport.byKind || {}).sort((a, b) => b[1] - a[1]).map(([kind, n]) => (
              <div key={kind} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', border: `1px solid ${t.border}`, borderRadius: 8, background: t.bg }}>
                <span style={{ fontSize: 12, color: t.muted, fontFamily: 'monospace' }}>{kind}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: t.textStrong, fontFamily: B.serif }}>{n.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin login gate ─────────────────────────────────────────────────────────
function AdminLoginGate({ onUnlock }) {
  const [pw, setPw]   = useState('');
  const [err, setErr] = useState(false);
  const t = THEMES.dark;
  const submit = e => {
    e.preventDefault();
    if (pw === '2045') { onUnlock(); }
    else { setErr(true); setPw(''); }
  };
  return (
    <div style={{ minHeight: '100vh', background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: B.sans }}>
      <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 360, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, background: B.teal, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><polygon points="14,2 6,14 11,14 8,22 20,10 14,10 18,2" fill="white"/></svg>
        </div>
        <h1 style={{ fontFamily: B.serif, fontSize: 22, fontWeight: 800, color: t.textStrong, marginBottom: 6 }}>BoltSync Admin</h1>
        <p style={{ fontSize: 14, color: t.muted, marginBottom: 28 }}>Enter your password to continue</p>
        <form onSubmit={submit}>
          <input
            type="password" placeholder="Password" autoFocus
            value={pw} onChange={e => { setPw(e.target.value); setErr(false); }}
            style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${err ? B.red : t.border}`, borderRadius: 10, fontSize: 16, textAlign: 'center', outline: 'none', marginBottom: 12, boxSizing: 'border-box', fontFamily: B.sans, background: t.bg, color: t.textStrong }}
          />
          {err && <p style={{ color: B.red, fontSize: 13, marginBottom: 12 }}>Incorrect password</p>}
          <button type="submit" style={{ width: '100%', padding: '12px', background: B.teal, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: B.sans }}>
            Enter →
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  if (portalSlug) return <PortalApp />;
  return <AdminApp />;
}

function AdminApp() {
  const [adminAuth, setAdminAuth] = useState(() => localStorage.getItem('admin-auth') === '2045');
  const [darkMode, setDarkMode]       = useState(() => localStorage.getItem('boltsync-theme') !== 'light');
  const [status, setStatus]           = useState(null);
  const [clients, setClients]         = useState([]);
  const [logs, setLogs]               = useState([]);
  const [callLogs, setCallLogs]       = useState([]);
  const [isDemo, setIsDemo]           = useState(false);
  const [showAddClient, setShowAdd]   = useState(false);
  const [syncing, setSyncing]         = useState({});
  const [activeTab, setActiveTab]     = useState('dashboard');
  const [settingUp, setSettingUp]     = useState({});
  const [setupResult, setSetupResult] = useState({});
  const [brandingEdit, setBrandingEdit] = useState({});   // { [id]: { logoUrl, tagline } }
  const [brandingSaving, setBrandingSaving] = useState({});
  const [archiveState, setArchiveState]         = useState({});
  const [phoneBackfill, setPhoneBackfill]       = useState({});

  const t = darkMode ? THEMES.dark : THEMES.light;

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('boltsync-theme', next ? 'dark' : 'light');
  };

  const load = useCallback(async () => {
    try {
      const [st, cl, lg, vl] = await Promise.all([api.getStatus(), api.getClients(), api.getLogs(), api.getCallLogs()]);
      setStatus(st); setClients(cl.clients || []); setLogs(lg.logs || []); setCallLogs(vl.logs || []); setIsDemo(false);
    } catch { setIsDemo(true); }
  }, []);

  useEffect(() => { load(); const timer = setInterval(load, 30000); return () => clearInterval(timer); }, [load]);

  // Read jane_connected / jane_error from URL params (set by OAuth callback)
  const [janeBanner, setJaneBanner] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('jane_connected')) return { type: 'success', msg: `Jane connected for ${p.get('jane_connected')}` };
    if (p.get('jane_error'))     return { type: 'error',   msg: `Jane error: ${p.get('jane_error')}` };
    return null;
  });
  useEffect(() => {
    if (janeBanner) {
      // Clean URL without reloading
      window.history.replaceState({}, '', window.location.pathname);
      const t = setTimeout(() => setJaneBanner(null), 6000);
      return () => clearTimeout(t);
    }
  }, [janeBanner]);

  if (!adminAuth) return <AdminLoginGate onUnlock={() => { localStorage.setItem('admin-auth', '2045'); setAdminAuth(true); }} />;

  const handleAdd    = async (data) => { await api.addClient(data); load(); };
  const handleSync   = async (id) => {
    setSyncing(s => ({ ...s, [id]: true }));
    try { await api.syncClient(id); setTimeout(load, 1200); } finally { setSyncing(s => ({ ...s, [id]: false })); }
  };
  const handleToggle    = async (id, active) => { await api.updateClient(id, { isActive: !active }); load(); };
  const handleSetupFields = async (id) => {
    setSettingUp(s => ({ ...s, [id]: true }));
    setSetupResult(s => ({ ...s, [id]: null }));
    try {
      const res = await api.setupGhlFields(id);
      setSetupResult(s => ({ ...s, [id]: `✓ ${res.created} created, ${res.skipped} already existed${res.failed > 0 ? `, ${res.failed} failed` : ''}` }));
    } catch (err) {
      setSetupResult(s => ({ ...s, [id]: `✗ ${err.message}` }));
    } finally { setSettingUp(s => ({ ...s, [id]: false })); }
  };

  const handleSaveBranding = async (id) => {
    const { logoUrl = '', tagline = '' } = brandingEdit[id] || {};
    setBrandingSaving(s => ({ ...s, [id]: true }));
    try {
      await api.updateClient(id, { logo_url: logoUrl, social_tagline: tagline });
      setBrandingEdit(s => ({ ...s, [id]: { ...s[id], saved: true } }));
      setTimeout(() => setBrandingEdit(s => ({ ...s, [id]: { ...s[id], saved: false } })), 2000);
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally { setBrandingSaving(s => ({ ...s, [id]: false })); }
  };

  const handleArchiveInactive = async (id, name) => {
    if (!window.confirm(`Dry run first: check how many inactive patients ${name} has (no activity in 10+ years)?`)) return;
    setArchiveState(s => ({ ...s, [id]: { status: 'scanning', message: 'Scanning…' } }));
    try {
      const dry = await api.archiveInactiveDryRun(id, 10);
      const proceed = window.confirm(`Found ${dry.archived} patients with no activity since ${new Date(dry.cutoff).getFullYear()}.\n\nArchive them now? They will be hidden from Kayli's searches but can be unarchived in Cliniko if needed.`);
      if (!proceed) { setArchiveState(s => ({ ...s, [id]: null })); return; }
      setArchiveState(s => ({ ...s, [id]: { status: 'archiving', message: 'Archiving…' } }));
      const result = await api.archiveInactive(id, 10);
      setArchiveState(s => ({ ...s, [id]: { status: 'done', message: result.message } }));
    } catch (err) {
      setArchiveState(s => ({ ...s, [id]: { status: 'error', message: err.message } }));
    }
  };

  const handleBackfillPhones = async (id, name) => {
    setPhoneBackfill(s => ({ ...s, [id]: { running: true, message: 'Backfilling…' } }));
    try {
      const res = await api.backfillPhones(id);
      setPhoneBackfill(s => ({ ...s, [id]: { running: false, message: res.message, hasMore: res.has_more } }));
    } catch (err) {
      setPhoneBackfill(s => ({ ...s, [id]: { running: false, message: `Error: ${err.message}` } }));
    }
  };

  const successCount = logs.filter(l => l.status === 'success').length;
  const errorCount   = logs.filter(l => l.status === 'error').length;
  const totalRecords = logs.reduce((n, l) => n + (l.records_synced || 0), 0);

  // Shared style helpers using current theme
  const card     = { background: t.card, borderRadius: 14, border: `1px solid ${t.border}`, padding: '24px 28px', marginBottom: 20, boxShadow: t.shadow };
  const cardHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 };
  const th       = { textAlign: 'left', padding: '10px 14px', borderBottom: `1px solid ${t.border}`, color: t.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' };
  const td       = { padding: '13px 14px', borderBottom: `1px solid ${t.border}`, color: t.text, verticalAlign: 'middle' };
  const ghostBtn = { padding: '7px 14px', borderRadius: 8, border: `1px solid ${t.border}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: 'transparent', color: t.muted, fontFamily: B.sans };
  const primaryBtn = { padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: B.teal, color: B.navy, fontFamily: B.sans };
  const dangerBtn  = { padding: '7px 14px', borderRadius: 8, border: `1px solid ${B.redBorder}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: B.redLight, color: B.red, fontFamily: B.sans };

  return (
    <div style={{ minHeight: '100vh', background: t.bg, color: t.text, fontFamily: B.sans, fontSize: 15, lineHeight: 1.6 }}>
      <style>{`
        .bs-tagline   { font-size:12px; color:rgba(255,255,255,0.3); }
        .bs-stats     { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-bottom:24px; }
        .bs-main      { max-width:1200px; margin:0 auto; padding:32px 24px 60px; }
        .bs-card      { border-radius:14px; padding:24px 28px; margin-bottom:20px; }
        .bs-tbl-wrap  { overflow-x:auto; -webkit-overflow-scrolling:touch; }
        .bs-header    { background:#080F1C; padding:0 32px; height:64px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100; }
        .bs-hdr-left  { display:flex; align-items:center; gap:14px; }
        .bs-hdr-right { display:flex; align-items:center; gap:10px; }
        .bs-add-lbl   { display:inline; }
        @media (max-width: 768px) {
          .bs-stats     { grid-template-columns:repeat(2,1fr); gap:12px; }
          .bs-main      { padding:16px 12px 48px; }
          .bs-card      { padding:16px 14px !important; border-radius:12px !important; }
          .bs-tagline   { display:none; }
          .bs-header    { padding:0 14px !important; height:56px !important; }
          .bs-add-lbl   { display:none; }
          .bs-modal-g2  { grid-template-columns:1fr !important; }
        }
        @media (max-width: 480px) {
          .bs-stats    { grid-template-columns:repeat(2,1fr); gap:10px; }
        }
      `}</style>

      {/* ── Header ── */}
      <header className="bs-header">
        <div className="bs-hdr-left">
          <Logo />
          <span className="bs-tagline">PMS ↔ GoHighLevel</span>
        </div>
        <div className="bs-hdr-right">
          <ThemeToggle dark={darkMode} onToggle={toggleTheme} />
          <Badge status={isDemo ? 'Disconnected' : 'Live'} />
          <button style={{ ...primaryBtn, padding: '8px 18px' }} onClick={() => setShowAdd(true)}>
            + <span className="bs-add-lbl">Add Client</span>
          </button>
        </div>
      </header>

      {/* ── Jane OAuth banner ── */}
      {janeBanner && (
        <div style={{
          background: janeBanner.type === 'success' ? B.tealLight : B.redLight,
          color:       janeBanner.type === 'success' ? B.tealDark  : B.red,
          textAlign: 'center', padding: '10px 24px', fontSize: 13,
          borderBottom: `1px solid ${janeBanner.type === 'success' ? B.tealBorder : B.redBorder}`,
        }}>
          {janeBanner.type === 'success' ? '✓' : '⚠️'} {janeBanner.msg}
        </div>
      )}

      {/* ── Demo banner ── */}
      {isDemo && (
        <div style={{ background: 'rgba(245,158,11,0.08)', color: B.amber, textAlign: 'center', padding: '10px 24px', fontSize: 13, borderBottom: '1px solid rgba(245,158,11,0.2)' }}>
          ⚠️ Not connected — check that <code>REACT_APP_API_SECRET</code> is set in Vercel environment variables.
        </div>
      )}

      <main className="bs-main">

        {/* ── Stats ── */}
        <div className="bs-stats">
          {[
            { label: 'Active Clients',   value: status?.activeClients ?? '—', bg: 'linear-gradient(135deg, #00D4A0, #00a87e)' },
            { label: 'Successful Syncs', value: successCount,                  bg: 'linear-gradient(135deg, #10b981, #059669)' },
            { label: 'Recent Errors',    value: errorCount,                    bg: 'linear-gradient(135deg, #ef4444, #dc2626)' },
            { label: 'Records Synced',   value: totalRecords.toLocaleString(), bg: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
          ].map((stat, i) => (
            <div key={i} style={{ background: stat.bg, borderRadius: 14, padding: '20px 24px', boxShadow: t.shadow }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1, fontFamily: B.serif }}>{stat.value}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 6, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* ── Tab navigation ── */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${t.border}`, marginBottom: 24 }}>
          {[
            { key: 'dashboard',  label: 'Dashboard' },
            { key: 'mirror',     label: 'Mirror Health' },
            { key: 'progress',   label: '📊 Progress Reports' },
          ].map(tb => (
            <button key={tb.key} onClick={() => setActiveTab(tb.key)} style={{
              padding: '9px 22px', borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer',
              fontFamily: B.sans, fontSize: 13, fontWeight: 600, background: 'transparent',
              color: activeTab === tb.key ? B.teal : t.muted,
              borderBottom: activeTab === tb.key ? `2px solid ${B.teal}` : '2px solid transparent',
              marginBottom: -1, transition: 'color 0.15s',
            }}>{tb.label}</button>
          ))}
        </div>

        {/* ── Mirror Health tab ── */}
        {activeTab === 'mirror' && (
          <MirrorHealthSection t={t} />
        )}

        {/* ── Progress Reports tab ── */}
        {activeTab === 'progress' && (
          <ProgressReportsSection clients={clients} t={t} />
        )}

        {/* ── Dashboard tab ── */}
        {activeTab === 'dashboard' && <>

        {/* ── Clients ── */}
        <div className="bs-card" style={{ background: t.card, border: `1px solid ${t.border}`, marginBottom: 20, boxShadow: t.shadow }}>
          <div style={cardHead}>
            <span style={{ fontSize: 15, fontWeight: 700, color: t.textStrong, fontFamily: B.serif }}>Clients</span>
            <button style={ghostBtn} onClick={load}>↻ Refresh</button>
          </div>
          {clients.length === 0 ? (
            <Empty icon="🏥" title="No clients yet" sub='Click "+ Add Client" to connect your first clinic.' t={t} />
          ) : (
            <div className="bs-tbl-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 560 }}>
              <thead><tr>
                {['Practice','Location','PMS','Status','Contacts','Last Sync','Actions'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>{clients.map(c => (
                <tr key={c.id}>
                  <td style={td}>
                    <span style={{ fontWeight: 700, color: t.textStrong, fontFamily: B.serif }}>{c.name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: t.muted, marginTop: 2 }}>ID: {c.id}</span>
                  </td>
                  <td style={td}>{c.location || <span style={{ color: t.muted }}>—</span>}</td>
                  <td style={td}><PmsBadge /></td>
                  <td style={td}><Badge status={c.is_active ? (c.last_sync_status === 'success' ? 'Synced' : c.last_sync_status || 'Pending') : 'Paused'} /></td>
                  <td style={td}>{(c.total_contacts || 0).toLocaleString()}</td>
                  <td style={td}>{c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : <span style={{ color: t.muted }}>Never</span>}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={primaryBtn} onClick={() => handleSync(c.id)} disabled={syncing[c.id]}>
                        {syncing[c.id] ? 'Syncing…' : 'Sync Now'}
                      </button>
                      <button style={c.is_active ? dangerBtn : ghostBtn} onClick={() => handleToggle(c.id, c.is_active)}>
                        {c.is_active ? 'Pause' : 'Resume'}
                      </button>
                      <button style={{ ...ghostBtn, fontSize: 12 }} onClick={() => handleSetupFields(c.id)} disabled={settingUp[c.id]} title="Create all ClinicBolt custom fields in this GHL sub-account">
                        {settingUp[c.id] ? 'Creating…' : 'Setup GHL Fields'}
                      </button>
                    </div>
                    {setupResult[c.id] && (
                      <div style={{ fontSize: 11, marginTop: 6, color: setupResult[c.id].startsWith('✓') ? B.teal : B.red }}>
                        {setupResult[c.id]}
                      </div>
                    )}
                    {/* Phone backfill */}
                    <div style={{ marginTop: 6 }}>
                      <button
                        style={{ ...ghostBtn, fontSize: 11 }}
                        onClick={() => handleBackfillPhones(c.id, c.name)}
                        disabled={phoneBackfill[c.id]?.running}
                        title="Fill missing phone numbers in patient_map so Kayli can look up callers by phone"
                      >
                        {phoneBackfill[c.id]?.running ? 'Backfilling…' : 'Backfill Phone Numbers'}
                      </button>
                      {phoneBackfill[c.id]?.message && (
                        <div style={{ fontSize: 11, marginTop: 4, color: phoneBackfill[c.id]?.message?.startsWith('Error') ? B.red : B.teal }}>
                          {phoneBackfill[c.id].message}
                          {phoneBackfill[c.id].hasMore && (
                            <button style={{ marginLeft: 8, fontSize: 11, background: 'none', border: 'none', color: B.teal, cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => handleBackfillPhones(c.id, c.name)}>Run again →</button>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Branding */}
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <input
                        placeholder="Logo URL (https://…)"
                        value={brandingEdit[c.id]?.logoUrl ?? (c.logo_url || '')}
                        onChange={e => setBrandingEdit(s => ({ ...s, [c.id]: { ...s[c.id], logoUrl: e.target.value } }))}
                        style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: `1px solid ${t.border}`, background: t.bg, color: t.text, width: '100%', boxSizing: 'border-box' }}
                      />
                      <input
                        placeholder="Social tagline (e.g. See real results…)"
                        value={brandingEdit[c.id]?.tagline ?? (c.social_tagline || '')}
                        onChange={e => setBrandingEdit(s => ({ ...s, [c.id]: { ...s[c.id], tagline: e.target.value } }))}
                        style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: `1px solid ${t.border}`, background: t.bg, color: t.text, width: '100%', boxSizing: 'border-box' }}
                      />
                      <button
                        style={{ ...ghostBtn, fontSize: 11, alignSelf: 'flex-start', color: brandingEdit[c.id]?.saved ? B.teal : t.muted }}
                        onClick={() => handleSaveBranding(c.id)}
                        disabled={brandingSaving[c.id]}
                      >
                        {brandingSaving[c.id] ? 'Saving…' : brandingEdit[c.id]?.saved ? '✓ Saved' : 'Save Branding'}
                      </button>
                    </div>
                    {/* Archive inactive patients */}
                    <div style={{ marginTop: 8 }}>
                      <button
                        style={{ ...ghostBtn, fontSize: 11, color: B.red, borderColor: B.redBorder }}
                        onClick={() => handleArchiveInactive(c.id, c.name)}
                        disabled={archiveState[c.id]?.status === 'scanning' || archiveState[c.id]?.status === 'archiving'}
                        title="Archive patients with no Cliniko activity in 10+ years"
                      >
                        {archiveState[c.id]?.status === 'scanning'  ? 'Scanning…' :
                         archiveState[c.id]?.status === 'archiving' ? 'Archiving…' :
                         'Archive Inactive Patients'}
                      </button>
                      {archiveState[c.id]?.message && (
                        <div style={{ fontSize: 11, marginTop: 4, color: archiveState[c.id]?.status === 'error' ? B.red : B.teal }}>
                          {archiveState[c.id].message}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
            </div>
          )}
        </div>

        {/* ── Call Logs ── */}
        <CallLogsSection callLogs={callLogs} t={t} />

        {/* ── BoltStats: CAC Management ── */}
        <CacManager clients={clients} t={t} ghostBtn={ghostBtn} primaryBtn={primaryBtn} />

        {/* ── Sync Logs ── */}
        <div className="bs-card" style={{ background: t.card, border: `1px solid ${t.border}`, marginBottom: 20, boxShadow: t.shadow }}>
          <div style={cardHead}>
            <span style={{ fontSize: 15, fontWeight: 700, color: t.textStrong, fontFamily: B.serif }}>Recent Sync Logs</span>
          </div>
          {logs.length === 0 ? (
            <Empty icon="📋" title="No logs yet" sub="Logs will appear after the first sync runs." t={t} />
          ) : (
            <div className="bs-tbl-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 480 }}>
              <thead><tr>
                {['Client','Type','Status','Records','Time'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>{logs.slice(0, 25).map((log, i) => (
                <tr key={i}>
                  <td style={td}><span style={{ fontWeight: 600, color: t.textStrong }}>{log.client_name || '—'}</span></td>
                  <td style={td}><span style={{ textTransform: 'capitalize' }}>{log.sync_type}</span></td>
                  <td style={td}><Badge status={log.status} /></td>
                  <td style={td}>{log.records_synced}</td>
                  <td style={{ ...td, color: t.muted, fontSize: 13 }}>{log.started_at ? new Date(log.started_at).toLocaleString() : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ textAlign: 'center', fontSize: 12, color: t.muted, marginTop: 8 }}>
          BoltSync by <a href="https://clinicbolt.com" style={{ color: B.tealDark, textDecoration: 'none', fontWeight: 600 }}>ClinicBolt</a> · Clinic growth at the speed of now
        </div>

        </> /* end dashboard tab */}

      </main>

      {showAddClient && <AddClientModal onClose={() => setShowAdd(false)} onAdd={handleAdd} t={t} />}
    </div>
  );
}
